/**
 * Multi-tenant Management Dashboard
 * 
 * A comprehensive 3-level hierarchy management system:
 * - Tenants (top level)
 * - Locations/Stores (middle level) 
 * - Servers/Managers (bottom level)
 * 
 * Features role-based access control and full CRUD operations.
 */

"use client";

import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { locationPath, parseManagementPath, rootPath, tenantPath } from "./management/routes";
import { ManagerShiftRecord, ServerShiftsState } from "./management/types";
import ManagementHeader from "./management/ManagementHeader";
import NewTenantModal from "./management/NewTenantModal";
import InfoPopover from "./management/InfoPopover";
import TenantTree from "./management/TenantTree";
import RootPane from "./management/RootPane";
import TenantPane from "./management/TenantPane";
import LocationPane from "./management/LocationPane";
import { cls } from "./management/styles";
import { fetchApi } from "../../utils/api";
import { getAuthToken } from "../../services/authToken";
import { useAuth } from "../../contexts/AuthContext";
import { createPermissionChecker } from "../../utils/permissions";
import { fetchConsentReport, withdrawConsent } from "../../services/consentEvidenceService";
import { generateConsentEvidencePdf } from "./ConsentEvidencePdf";
import {
  Tenant,
  Location,
  Server,
  Manager,
  TenantCreateRequest,
  TenantUpdateRequest,
  LocationCreateRequest,
  LocationUpdateRequest,
  ServerCreateRequest,
  ServerUpdateRequest,
  ManagerCreateRequest,
  ManagerUpdateRequest,
  UserContext
} from "../../types/tenant";
import { TenantRole } from "../../types/prompt";
import { RoleNameLookup, serverRolesFromApi, serverSaveErrorMessage } from "./serverRoles";
import { ServerRolesField, TenantRolesStatus, rolesPayload } from "./ServerRolesField";
import { SUPPORTED_LANGUAGES } from "../../services/translationsAdminService";
import DemoSetupJobViewer, { DemoSetupJobData } from "./DemoSetupJobViewer";
import TenantLanguageDefaultCard from "../restaurant/admin/TenantLanguageDefaultCard";
import TenantTranslationLanguagesCard from "../restaurant/admin/TenantTranslationLanguagesCard";
import TenantCurriculumApprovalCard from "../restaurant/admin/TenantCurriculumApprovalCard";
import TenantDripControl from "./drip/TenantDripControl";
import {
  ASSISTANT_TOGGLE_HELP_BODY,
  ASSISTANT_TOGGLE_HELP_LABEL,
  ASSISTANT_TOGGLE_HELP_LEAD,
  ASSISTANT_TOGGLE_LABEL,
  ASSISTANT_TOGGLE_SAVING,
  assistantToggleSaveFailed,
} from "./assistant/assistantToggleCopy";
import { invalidateAssistantConfig } from "../../services/assistantService";
import {
  AUTO_COPY_TOGGLE_HELP_BODY,
  AUTO_COPY_TOGGLE_HELP_LABEL,
  AUTO_COPY_TOGGLE_HELP_LEAD,
  AUTO_COPY_TOGGLE_LABEL,
  AUTO_COPY_TOGGLE_SAVING,
  autoCopyToggleSaveFailed,
} from "./autoCopy/autoCopyToggleCopy";
import {
  DRIP_LOCATION_HELP,
  DRIP_LOCATION_INHERIT,
  DRIP_LOCATION_LEGEND,
  DRIP_LOCATION_OFF,
  DRIP_LOCATION_ON,
  DRIP_LOCATION_RATE_ARIA,
  DRIP_RATE_INVALID,
  DRIP_RATE_SUFFIX,
  dripLocationInheritDetail,
  dripLocationTurnOffConfirm,
  dripSaveFailed,
  dripTenantTurnOffConfirm,
} from "./drip/dripCopy";
import {
  DRIP_RATE_MAX,
  DRIP_RATE_MIN,
  LocationDripBody,
  LocationDripChoice,
  effectiveLocationDrip,
  locationDripBody,
  locationDripChanged,
  locationDripChoice,
  parseDripRate,
} from "./drip/dripSettings";

/**
 * feat-255-pass-servers-thru-content: the 200 body of
 * `POST .../servers/{id}/complete-learn-progress`. This shape is the backend
 * slice's contract — the four counts are what the success line is built from.
 */
export interface CompleteLearnProgressResponse {
  message?: string;
  drills_total?: number;
  completions_inserted?: number;
  already_complete?: number;
  lessons_with_no_active_drills?: number;
}

const countNoun = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * feat-255-pass-servers-thru-content: the inline success line for a completed
 * mark-all-complete run.
 *
 * `lessons_with_no_active_drills` is mentioned only when it is non-zero: an
 * active lesson with no active drills can never be complete (the fail-closed
 * rule in the backend's `services/learn_tracks.py`), so its track stays locked
 * even after a successful call. Naming the count here is what stops a tester
 * concluding the button silently failed.
 *
 * Exported for direct unit assertion — reading counts out of a response is the
 * part with arithmetic in it.
 */
export function buildCompleteLearnNotice(result: CompleteLearnProgressResponse | null | undefined): string {
  const inserted = Number(result?.completions_inserted ?? 0);
  const already = Number(result?.already_complete ?? 0);
  const emptyLessons = Number(result?.lessons_with_no_active_drills ?? 0);

  let notice = `Marked ${countNoun(inserted, 'drill', 'drills')} complete (${already} already complete).`;
  if (emptyLessons > 0) {
    notice += ` ${countNoun(emptyLessons, 'active lesson has', 'active lessons have')} no active drills and cannot be completed, so ${emptyLessons === 1 ? 'its track' : 'their tracks'} will stay locked.`;
  }
  return notice;
}

// feat-301 QA: the blank server form. A FRESH object every call (roles is an
// array). Opening "Add Server" and closing the modal (✕ / Cancel) both reset to
// it, so a cancelled Edit can never leak another server's fields — including its
// roles — into a new server's create payload.
const emptyNewServer = () => ({ first_name: "", last_name: "", phone: "", pin: "", default_role: "", roles: [] as string[], notifications: false, demo: false, test: false, preferred_locale: "en", location_id: "", is_multi_location: false });

export default function TenantDashboard() {
  const { user, logout, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  // feat-307 (D1): the selected level comes from the URL, never from local state,
  // so a deep link, a reload and browser back/forward all land on the same pane.
  const { pathname } = useLocation();
  const { tenantId: routeTenantId, locationId: routeLocationId } = parseManagementPath(pathname);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // feat-255-pass-servers-thru-content: inline success line for the Progress
  // section's "Mark all complete". Lives next to `error` in the edit-server
  // modal and is cleared on the same events `error` is — dialog open, and the
  // start of the next attempt.
  const [learnCompleteNotice, setLearnCompleteNotice] = useState<string | null>(null);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    // Try to get token from user object first (Cognito access token)
    if (user?.accessToken) {
      return { Authorization: `Bearer ${user.accessToken}` };
    }
    
    // Fallback to localStorage (Cognito access token, then legacy 'token')
    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication required. Please log in again.");
    }
    return { Authorization: `Bearer ${token}` };
  };
  
  // Form states
  const [newTenant, setNewTenant] = useState({ tenant_name: "", description: "" });
  // feat-196 (B2) — in-flight / error state for the Live-client checkbox. Scoped
  // per-tenant so one tenant's failing write doesn't disable every other row's
  // control, and kept OUT of the shared `error` banner because this write is the
  // feature's critical path (no migration seed) and its failure needs to be
  // legible right beside the control that failed.
  const [liveClientPendingTenantId, setLiveClientPendingTenantId] = useState<string | null>(null);
  const [liveClientError, setLiveClientError] = useState<string | null>(null);
  // feat-303-curriculum-drip-release — in-flight / error state for the tenant
  // drip block, per tenant and inline (same reasoning as Live client above).
  const [dripPendingTenantId, setDripPendingTenantId] = useState<string | null>(null);
  const [dripError, setDripError] = useState<{ tenantId: string; message: string } | null>(null);
  // feat-305-chat-machinery-server-hist — in-flight / error state for the
  // Manager assistant switch, per tenant and inline (same reasoning as above).
  const [assistantPendingTenantId, setAssistantPendingTenantId] = useState<string | null>(null);
  const [assistantError, setAssistantError] = useState<{ tenantId: string; message: string } | null>(null);
  // feat-354-auto-copy-lessons — in-flight / error state for the lesson
  // auto-copy switch, per tenant and inline (same reasoning as above).
  const [autoCopyPendingTenantId, setAutoCopyPendingTenantId] = useState<string | null>(null);
  const [autoCopyError, setAutoCopyError] = useState<{ tenantId: string; message: string } | null>(null);
  // feat-303 — the Edit Location modal's drip override form (Edit only, Admin
  // only). Kept apart from `newLocation` so the Add path can never carry it.
  const [locationDripForm, setLocationDripForm] = useState<{
    choice: LocationDripChoice;
    rate: string;
    invalid: boolean;
  }>({ choice: 'inherit', rate: '1', invalid: false });
  const locationDripRateRef = useRef<HTMLInputElement | null>(null);
  // feat-094: `bespoke_go_live_date` holds the form value for the go-live cutoff
  // field. "" means cleared / "Not yet live"; an ISO YYYY-MM-DD string means set.
  // feat-320: `recording_start_date` follows the same convention ("" = "Not yet recording").
  const [newLocation, setNewLocation] = useState({ name: "", timezone: "", auth_method: "phone_otp", recording_start_date: "", bespoke_go_live_date: "" });
  const [newServer, setNewServer] = useState(emptyNewServer);
  const [newManager, setNewManager] = useState<{
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    role: 'Corporate' | 'Manager';
  }>({ 
    email: "", 
    first_name: "", 
    last_name: "", 
    phone: "",
    role: "Manager" 
  });
  const [managerModalRole, setManagerModalRole] = useState<'Corporate' | 'Manager'>("Manager");
  const [tenantRoles, setTenantRoles] = useState<TenantRole[]>([]);
  // feat-301: the roles fetch's outcome. 'error' must NEVER read as "no roles"
  // (design R9): the field goes read-only and the payload omits roles entirely.
  const [tenantRolesStatus, setTenantRolesStatus] = useState<TenantRolesStatus>("loading");
  
  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedTenants, setExpandedTenants] = useState<{ [key: string]: boolean }>({});
  const [expandedLocations, setExpandedLocations] = useState<{ [key: string]: boolean }>({});
  // feat-307 — set true once GET /api/tenant has succeeded, so an unknown
  // :tenantId is only redirected away when the list really lacks it.
  const [tenantsListLoaded, setTenantsListLoaded] = useState(false);
  // feat-307 (M9) — "New tenant" is a modal now, not a full-width panel.
  const [newTenantOpen, setNewTenantOpen] = useState(false);
  // feat-307 (D10) — the sidebar tree's drawer below the lg breakpoint.
  const [treeOpen, setTreeOpen] = useState(false);
  const [loadedTenants, setLoadedTenants] = useState<{ [tenantId: string]: boolean }>({});
  const [loadedLocations, setLoadedLocations] = useState<{ [locationId: string]: boolean }>({});
  const [loadingTenants, setLoadingTenants] = useState<{ [tenantId: string]: boolean }>({});
  const [loadingLocations, setLoadingLocations] = useState<{ [locationId: string]: boolean }>({});
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [modalType, setModalType] = useState<'location' | 'server' | 'manager' | 'manager-shift' | 'server-audit' | null>(null);
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [editingServerId, setEditingServerId] = useState<string | null>(null);

  // feat-022-consent-evidence: consent-report download + withdraw-consent flow.
  // `generatingReportLocationId` disables the per-location "Consent evidence"
  // button while its PDF builds. The withdraw modal captures a mandatory reason
  // (ADR 0001 §6) before calling the withdraw endpoint.
  const [generatingReportLocationId, setGeneratingReportLocationId] = useState<string | null>(null);
  const [withdrawTarget, setWithdrawTarget] = useState<
    { tenantId: string; locationId: string; serverId: string; serverName: string } | null
  >(null);
  const [withdrawReason, setWithdrawReason] = useState('');

  // XP Audit modal state
  const [auditingServerId, setAuditingServerId] = useState<string | null>(null);
  const [auditingServerName, setAuditingServerName] = useState<string>("");
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditData, setAuditData] = useState<{
    entries: Array<{
      id: string;
      amount: number;
      balance_after: number | null;
      source: string;
      source_id: string | null;
      description: string | null;
      created: string | null;
      /**
       * feat-198 (ADR 0001 §D-H): renamed from `lesson_title`/`lesson_emoji`.
       * These are sourced from the level-2 row — the product's DRILL — so under
       * the rename the old keys would have said "lesson" while holding Drill
       * data. `xp-audit` is NOT an aliased path, so there is no compat window:
       * these keys change the moment the backend deploys, and a stale FE would
       * not throw — the `e.drill_title` guard below just goes falsy and the row
       * silently renders the generic description instead of the drill name.
       */
      drill_title: string | null;
      drill_emoji: string | null;
      quiz_score: number | null;
      quiz_total: number | null;
      interaction_type: string | null;
      interaction_shift_id: string | null;
      gift_label: string | null;
      gift_type: string | null;
      gift_from_name: string | null;
      gift_to_name: string | null;
      reward_name: string | null;
      reward_emoji: string | null;
    }>;
    total_earned: number;
    total_spent: number;
    net_balance: number;
  } | null>(null);

  // Manager shift states
  const [managerShifts, setManagerShifts] = useState<{ [locationId: string]: ManagerShiftRecord[] }>({});
  const [expandedManagerShifts, setExpandedManagerShifts] = useState<{ [msId: string]: boolean }>({});
  const [serverShiftsForManager, setServerShiftsForManager] = useState<{ [msId: string]: ServerShiftsState }>({});
  const [editingManagerShiftId, setEditingManagerShiftId] = useState<string | null>(null);
  const [newManagerShift, setNewManagerShift] = useState({
    manager_id: '',
    server_ids: [] as string[],
    shift_type: 'Lunch',
    start_time: '',
    end_time: '',
    notes: ''
  });
  const [editingItem, setEditingItem] = useState<{
    type: 'tenant' | 'location' | 'server' | 'manager';
    id: string;
    data: any;
  } | null>(null);
  const [managerError, setManagerError] = useState<string | null>(null);

  // Demo setup job viewer state
  const [tenantIdsWithJobs, setTenantIdsWithJobs] = useState<Set<string>>(new Set());
  const [viewingDemoJob, setViewingDemoJob] = useState<{
    tenantId: string;
    tenantName: string;
    loading: boolean;
    error: string | null;
    data: DemoSetupJobData | null;
  } | null>(null);

  const [passwordPopup, setPasswordPopup] = useState<{
    show: boolean;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    role: string;
    password: string;
  } | null>(null);

  // Get user context and permissions
  // Try tenantContext first, then fall back to managementUser
  const userContext: UserContext | null = user?.tenantContext ? {
    email: user.tenantContext.email,
    role: user.tenantContext.role as 'Admin' | 'Corporate' | 'Manager',
    tenantId: user.tenantContext.tenantId && user.tenantContext.tenantId.trim() !== '' ? user.tenantContext.tenantId : undefined,
    locationId: user.tenantContext.locationId
  } : user?.managementUser ? {
    email: user.managementUser.email,
    role: (user.managementUser.role.charAt(0).toUpperCase() + user.managementUser.role.slice(1).toLowerCase()) as 'Admin' | 'Corporate' | 'Manager',
    tenantId: user.managementUser.tenant_id && user.managementUser.tenant_id.trim() !== '' ? user.managementUser.tenant_id : undefined,
    locationId: user.managementUser.location_id
  } : null;
  const permissions = userContext ? createPermissionChecker(userContext) : null;
  // feat-094: the go-live cutoff field is Admin-only-editable (RBAC resolution
  // 0002); Corporate/Manager see it read-only. When there is no resolvable user
  // context (dev fallback), treat as Admin so the field is editable locally.
  const isAdmin = userContext ? userContext.role === 'Admin' : true;

  // feat-196 (security-01 G11) — the Admin gate for the "Live client" control,
  // resolved FAIL-CLOSED. Deliberately NOT `isAdmin` above: that one defaults to
  // TRUE when `userContext` can't be resolved (a feat-094 dev fallback), and
  // chaining a fail-OPEN visibility gate to a write produced the G11 finding —
  // a Corporate Manager whose context fails to resolve would see this checkbox,
  // tick it, and (under the silently-ignored variant of AC8) get a 200 back and
  // believe their tenant was live when it wasn't.
  //
  // An unresolvable context HIDES the control. That is the discriminating case QA
  // tests for AC12 — Admin-sees / Corporate-doesn't passes even against the
  // fail-open flag, so it proves nothing about this gate.
  //
  // This is PRESENTATION, not the security control. The boundary is the backend's
  // 403 in `update_tenant` (ADR 0001 §3). Never treat this checkbox as the gate —
  // and note `bespoke_go_live_date`, the nearest field on this same screen, is
  // "Admin-only" in the FRONTEND ONLY (`update_location` has zero `is_admin()`
  // checks), which is exactly the shape NOT to copy.
  const isAdminForLiveClient = userContext?.role === 'Admin';

  // Debug logging
  console.log('TenantDashboard - user:', user);
  console.log('TenantDashboard - user.managementUser:', user?.managementUser);
  console.log('TenantDashboard - user.tenantContext:', user?.tenantContext);
  console.log('TenantDashboard - userContext:', userContext);
  console.log('TenantDashboard - permissions:', permissions);
  
  // For development, if no permissions are available, create a fallback admin user
  // Make sure fallback functions accept the same parameters as the real permission checker
  const fallbackPermissions = permissions || {
    canCreateTenant: true,
    canEditTenant: (_tenantId: string) => true,
    canDeleteTenant: (_tenantId: string) => true,
    canCreateLocation: (_tenantId: string) => true,
    canEditLocation: (_tenantId: string, _locationId: string) => true,
    canDeleteLocation: (_tenantId: string, _locationId: string) => true,
    canCreateServer: (_tenantId: string, _locationId: string) => true,
    canEditServer: (_tenantId: string, _locationId: string, _serverId: string) => true,
    canDeleteServer: (_tenantId: string, _locationId: string, _serverId: string) => true,
    canCreateManager: (_tenantId: string, _locationId?: string) => true,
    canEditManager: (_managerId: string) => true,
    canDeleteManager: (_managerId: string) => true
  };
  

  // Handle back navigation
  const handleBackClick = () => {
    // feat-318-admin-ui-conveniences: Admins reach this page from the Bespoke
    // Admin hub, so Back always returns there. Fail-closed on an unresolved
    // context (isAdminForLiveClient, not the fail-open isAdmin) because
    // /restaurant/internal is <AdminRoute> and would bounce anyone else.
    if (isAdminForLiveClient) {
      navigate('/restaurant/internal');
      return;
    }
    // Try to go back in history, fallback to server scorecard if no history
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/restaurant/server-scorecard');
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/scorecard-login', { replace: true });
  };

  // Map a raw location response (no nested data) into the Location shape used in state.
  const mapLocationShell = (location: any): Location => ({
    id: location.id,
    name: location.name,
    tenantId: location.tenant_id,
    createdAt: location.created,
    timezone: location.timezone || 'America/Los_Angeles',
    authMethod: location.auth_method || 'phone_otp',
    // feat-094: read-back of the go-live cutoff (LocationResponse.bespoke_go_live_date,
    // ISO YYYY-MM-DD or null). null → "Not yet live" in the Edit Location form.
    // feat-320: first day crew record (LocationResponse.recording_start_date,
    // ISO YYYY-MM-DD or null). null → "Not yet recording" in the Edit Location form.
    recordingStartDate: location.recording_start_date ?? null,
    bespokeGoLiveDate: location.bespoke_go_live_date ?? null,
    // feat-303: the drip OVERRIDE, null = inherit. `?? null` (not `||`) so an
    // explicit `false` survives as "Off for this location".
    dripEnabled: location.drip_enabled ?? null,
    dripLessonsPerDay: location.drip_lessons_per_day ?? null,
    servers: [],
    managers: [],
    shiftLeads: []
  });

  const mapServer = (s: any): Server => ({
    id: s.id,
    firstName: s.first_name,
    lastName: s.last_name,
    name: s.name,
    phone: s.phone || '',
    pin: s.pin || '',
    locationId: s.location_id,
    // feat-155 (ADR 0001 D4): seeds the Edit Server multi-location toggle. Safe
    // before the backend read-model lands — undefined coerces to false.
    isMultiLocation: !!s.is_multi_location,
    defaultRole: s.default_role || '',
    // feat-301: role set, primary first; [default_role] before slice 2 deploys.
    roles: serverRolesFromApi(s.roles, s.default_role),
    features: s.features || null,
    demo: s.demo || false,
    test: s.test || false,
    preferredLocale: s.preferred_locale || 'en',
    trainingComplete: !!s.training_complete,
    onboardingComplete: !!s.onboarding_complete,
    consentGiven: !!s.consent_given,
    consentGivenAt: s.consent_given_at || '',
    createdAt: s.created || ''
  });

  const mapManager = (m: any): Manager => ({
    id: m.id,
    email: m.email,
    firstName: m.first_name,
    lastName: m.last_name,
    phone: m.phone,
    role: m.role,
    tenantId: m.tenant_id,
    locationId: m.location_id,
    createdAt: m.created || ''
  });

  // Step 1: Fetch the flat list of tenants only. Nested data is loaded on demand.
  const fetchTenantsList = async () => {
    setLoading(true);
    try {
      const authHeader = getAuthHeaders();
      const rawRole = userContext?.role || user?.managementUser?.role;
      const role = rawRole ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase() : null;
      const tenantId = userContext?.tenantId || user?.managementUser?.tenant_id;

      const response = await fetchApi<any[]>("/api/tenant", { headers: authHeader });

      const tenantsToProcess = role === 'Admin'
        ? response
        : role === 'Corporate' || role === 'Manager'
          ? response.filter(tenant => tenant.tenant_id === tenantId)
          : [];

      // Preserve already-loaded nested data when refreshing the list (don't clobber expanded tenants).
      setTenants(prev => {
        const byId = new Map(prev.map(t => [t.tenantId, t]));
        return tenantsToProcess.map((tenant: any) => {
          const existing = byId.get(tenant.tenant_id);
          return {
            tenantId: tenant.tenant_id,
            tenantName: tenant.tenant_name,
            description: tenant.description,
            createdAt: tenant.created,
            // feat-037-spanish-onboarding: tenant-level default language (ADR 0003 Q4)
            defaultLanguage: tenant.default_language ?? existing?.defaultLanguage,
            // feat-196 (A4) — CLIENT STATUS read-back from GET /api/tenant. This is
            // the field the Live-client checkbox renders, and re-reading it after a
            // write is how an Admin confirms the write actually took. Coerced with
            // `!!` so a backend that hasn't shipped A4 yet reads as false (control
            // renders unticked) rather than undefined (React uncontrolled-input
            // warning + a checkbox that silently won't tick).
            isLive: !!tenant.is_live,
            // feat-303 — tenant drip release read-back (defaults: off, 1/day).
            dripEnabled: !!tenant.drip_enabled,
            dripLessonsPerDay:
              typeof tenant.drip_lessons_per_day === 'number' ? tenant.drip_lessons_per_day : 1,
            // feat-305 — manager assistant read-back (default off; `!!` so a
            // backend without the field reads as off, not uncontrolled).
            assistantEnabled: !!tenant.assistant_enabled,
            // feat-354 — lesson auto-copy read-back (default off; absent ⇒ off).
            learnAutoCopyEnabled: !!tenant.learn_auto_copy_enabled,
            locations: existing?.locations ?? [],
            corporateManagers: existing?.corporateManagers ?? []
          };
        });
      });
      setError(null);
      setTenantsListLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      console.error("Error fetching tenants:", err);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Fetch one tenant's locations and corporate managers.
  const fetchTenantDetails = async (tenantId: string) => {
    setLoadingTenants(prev => ({ ...prev, [tenantId]: true }));
    try {
      const authHeader = getAuthHeaders();
      const rawRole = userContext?.role || user?.managementUser?.role;
      const role = rawRole ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase() : null;
      const userLocationId = userContext?.locationId || user?.managementUser?.location_id;

      const [locationsResponse, corporateManagersResponse] = await Promise.all([
        fetchApi<any[]>(`/api/tenant/${tenantId}/locations`, { headers: authHeader }),
        fetchApi<any[]>(`/api/tenant/${tenantId}/managers`, { headers: authHeader })
      ]);

      const locationsToProcess = role === 'Manager' && userLocationId
        ? locationsResponse.filter(loc => loc.id === userLocationId)
        : locationsResponse;

      setTenants(prev => prev.map(t => {
        if (t.tenantId !== tenantId) return t;
        // Preserve any already-loaded nested server/manager data per location.
        const prevLocById = new Map(t.locations.map(l => [l.id, l]));
        return {
          ...t,
          locations: locationsToProcess.map((loc: any) => {
            const existing = prevLocById.get(loc.id);
            const shell = mapLocationShell(loc);
            return existing
              ? {
                  ...shell,
                  servers: existing.servers,
                  managers: existing.managers,
                  shiftLeads: existing.shiftLeads,
                  shiftLeadsError: existing.shiftLeadsError
                }
              : shell;
          }),
          corporateManagers: (corporateManagersResponse || []).map(mapManager)
        };
      }));
      setLoadedTenants(prev => ({ ...prev, [tenantId]: true }));
    } catch (err) {
      console.error(`Error fetching details for tenant ${tenantId}:`, err);
      setError(err instanceof Error ? err.message : "Failed to load tenant details");
    } finally {
      setLoadingTenants(prev => ({ ...prev, [tenantId]: false }));
    }
  };

  // Step 3: Fetch one location's servers and store managers.
  const fetchLocationDetails = async (tenantId: string, locationId: string) => {
    setLoadingLocations(prev => ({ ...prev, [locationId]: true }));
    try {
      const authHeader = getAuthHeaders();
      const [serversResponse, managersResponse, shiftLeadsResponse] = await Promise.all([
        fetchApi<any[]>(`/api/tenant/${tenantId}/locations/${locationId}/servers`, { headers: authHeader }),
        fetchApi<any[]>(`/api/tenant/${tenantId}/locations/${locationId}/managers`, { headers: authHeader }),
        // feat-321: caught on its own so a failing (or not-yet-deployed)
        // shift-leads endpoint never fails the location load — servers and
        // managers still render. null ⇒ the pane's error state.
        fetchApi<any[]>(`/api/tenant/${tenantId}/locations/${locationId}/shift-leads`, { headers: authHeader })
          .catch((err) => {
            console.error(`Error fetching shift leads for location ${locationId}:`, err);
            return null;
          })
      ]);

      setTenants(prev => prev.map(t => {
        if (t.tenantId !== tenantId) return t;
        return {
          ...t,
          locations: t.locations.map(loc => {
            if (loc.id !== locationId) return loc;
            return {
              ...loc,
              servers: (serversResponse || []).map(mapServer),
              managers: (managersResponse || []).map(mapManager),
              shiftLeads: (shiftLeadsResponse || []).map(mapManager),
              shiftLeadsError: shiftLeadsResponse === null
            };
          })
        };
      }));
      setLoadedLocations(prev => ({ ...prev, [locationId]: true }));
    } catch (err) {
      console.error(`Error fetching details for location ${locationId}:`, err);
      setError(err instanceof Error ? err.message : "Failed to load location details");
    } finally {
      setLoadingLocations(prev => ({ ...prev, [locationId]: false }));
    }
  };

  // Filter tenants based on user role
  const getFilteredTenants = (allTenants: Tenant[]): Tenant[] => {
    // Get role and IDs from userContext or directly from managementUser
    const rawRole = userContext?.role || user?.managementUser?.role;
    // Normalize role to handle case-insensitive comparison
    const role = rawRole ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase() : null;
    const tenantId = userContext?.tenantId || user?.managementUser?.tenant_id;
    const locationId = userContext?.locationId || user?.managementUser?.location_id;
    
    // If no role information, return empty array (don't show anything)
    if (!role) {
      console.warn('TenantDashboard - No role information available, returning empty tenants list');
      return [];
    }
    
    switch (role) {
      case 'Admin':
        // Admins see all tenants
        return allTenants;
      case 'Corporate':
        // Corporate managers see only their tenant
        if (!tenantId) {
          console.warn('TenantDashboard - Corporate manager has no tenantId');
          return [];
        }
        return allTenants.filter(tenant => tenant.tenantId === tenantId);
      case 'Manager':
        // Store managers see only their tenant and only their location
        if (!tenantId || !locationId) {
          console.warn('TenantDashboard - Manager has no tenantId or locationId', { tenantId, locationId });
          return [];
        }
        // Lazy loading: locations arrive asynchronously, so don't drop the tenant just
        // because its locations aren't loaded yet. Filter the location list when present.
        return allTenants
          .filter(tenant => tenant.tenantId === tenantId)
          .map(tenant => ({
            ...tenant,
            locations: tenant.locations.filter(location => location.id === locationId)
          }));
      default:
        console.warn('TenantDashboard - Unknown role:', role);
        return [];
    }
  };

  // Load data when auth finishes loading and user data is available
  useEffect(() => {
    // Wait for auth to finish loading before fetching tenants
    if (!authLoading && user && (user.managementUser || user.tenantContext)) {
      fetchTenantsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  // Admin-only: fetch the set of tenant_ids that have a demo_setup_job so we
  // can conditionally render the "View Demo Setup" button per tenant.
  useEffect(() => {
    const rawRole = userContext?.role || user?.managementUser?.role;
    const role = rawRole ? rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase() : null;
    if (role !== 'Admin') return;
    if (authLoading || !user) return;

    (async () => {
      try {
        const authHeader = getAuthHeaders();
        const res = await fetchApi<{ tenant_ids: string[] }>(
          "/api/admin/demo-setup/tenant-ids-with-jobs",
          { headers: authHeader }
        );
        setTenantIdsWithJobs(new Set(res.tenant_ids || []));
      } catch (err) {
        console.error("Failed to fetch demo setup tenant ids:", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const openDemoSetupViewer = async (tenantId: string, tenantName: string) => {
    setViewingDemoJob({ tenantId, tenantName, loading: true, error: null, data: null });
    try {
      const authHeader = getAuthHeaders();
      const data = await fetchApi<DemoSetupJobData>(
        `/api/admin/demo-setup/by-tenant/${tenantId}`,
        { headers: authHeader }
      );
      setViewingDemoJob({ tenantId, tenantName, loading: false, error: null, data });
    } catch (err) {
      setViewingDemoJob({
        tenantId,
        tenantName,
        loading: false,
        error: err instanceof Error ? err.message : "Failed to load demo setup job",
        data: null,
      });
    }
  };

  // ---------------------------------------------------------------------------
  // feat-307 (spec D1/D5) — route-driven selection.
  //
  // Develop expanded tenants and locations with "Show Details"/"Show" clicks and
  // auto-expanded the only tenant (Corporate/Manager) and any only-location. The
  // URL now carries the selection, so these effects load exactly what develop's
  // expand loaded, for the selected level only, and through the same functions:
  //   tenant selected   → fetchTenantDetails(tenantId)   (once, if not loaded)
  //   location selected → fetchLocationDetails + fetchManagerShifts (if not cached)
  //
  // SCOPE FIRST (D5 / B4). A deep link outside the role's scope is redirected to
  // the role's landing page BEFORE anything is fetched for it: the checks below
  // run on ids alone, and the fetches additionally require the id to be in the
  // role-filtered lists that fetchTenantsList / fetchTenantDetails already build.
  const scopeRawRole = userContext?.role || user?.managementUser?.role;
  const scopeRole = scopeRawRole
    ? scopeRawRole.charAt(0).toUpperCase() + scopeRawRole.slice(1).toLowerCase()
    : null;
  const scopeTenantId = userContext?.tenantId || user?.managementUser?.tenant_id || null;
  const scopeLocationId = userContext?.locationId || user?.managementUser?.location_id || null;

  const landingPath =
    scopeRole === 'Corporate' && scopeTenantId
      ? tenantPath(scopeTenantId)
      : scopeRole === 'Manager' && scopeTenantId && scopeLocationId
        ? locationPath(scopeTenantId, scopeLocationId)
        : rootPath();

  const routeInScope = (() => {
    if (!routeTenantId) return true;
    if (scopeRole === 'Admin') return true;
    if (scopeRole === 'Corporate') return !!scopeTenantId && routeTenantId === scopeTenantId;
    if (scopeRole === 'Manager') {
      // F5: a Manager may open their tenant's pane (the surfaces develop showed
      // them), and only their own location under it.
      return (
        !!scopeTenantId &&
        !!scopeLocationId &&
        routeTenantId === scopeTenantId &&
        (!routeLocationId || routeLocationId === scopeLocationId)
      );
    }
    return false;
  })();

  // Redirects: root → landing for Corporate/Manager; out-of-scope → landing;
  // an id that does not exist in the (role-filtered) data → landing / its tenant.
  useEffect(() => {
    if (authLoading || !scopeRole) return;
    if (!routeTenantId) {
      if (scopeRole !== 'Admin' && landingPath !== rootPath()) navigate(landingPath, { replace: true });
      return;
    }
    if (!routeInScope) {
      navigate(landingPath, { replace: true });
      return;
    }
    if (tenantsListLoaded && !tenants.some((t) => t.tenantId === routeTenantId)) {
      navigate(landingPath, { replace: true });
      return;
    }
    if (routeLocationId && loadedTenants[routeTenantId]) {
      const t = tenants.find((x) => x.tenantId === routeTenantId);
      if (t && !t.locations.some((l) => l.id === routeLocationId)) {
        navigate(tenantPath(routeTenantId), { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, scopeRole, routeTenantId, routeLocationId, routeInScope, landingPath, tenantsListLoaded, tenants, loadedTenants]);

  // Tenant selected → its locations + corporate managers (same call as develop's expand).
  const tenantFetchAttempt = useRef<string | null>(null);
  useEffect(() => {
    if (!routeTenantId || !routeInScope) {
      tenantFetchAttempt.current = null;
      return;
    }
    if (!tenants.some((t) => t.tenantId === routeTenantId)) return;
    setExpandedTenants((prev) =>
      prev[routeTenantId] && Object.keys(prev).length === 1 ? prev : { [routeTenantId]: true },
    );
    if (tenantFetchAttempt.current === routeTenantId) return;
    tenantFetchAttempt.current = routeTenantId;
    if (!loadedTenants[routeTenantId] && !loadingTenants[routeTenantId]) {
      fetchTenantDetails(routeTenantId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeTenantId, routeInScope, tenants, loadedTenants, loadingTenants]);

  // Location selected → its servers + managers, and its manager shifts.
  const locationFetchAttempt = useRef<string | null>(null);
  useEffect(() => {
    if (!routeTenantId || !routeLocationId || !routeInScope) {
      locationFetchAttempt.current = null;
      if (!routeLocationId) {
        setExpandedLocations((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      }
      return;
    }
    const t = tenants.find((x) => x.tenantId === routeTenantId);
    if (!t || !loadedTenants[routeTenantId]) return;
    if (!t.locations.some((l) => l.id === routeLocationId)) return; // redirect effect handles it
    setExpandedLocations((prev) =>
      prev[routeLocationId] && Object.keys(prev).length === 1 ? prev : { [routeLocationId]: true },
    );
    if (locationFetchAttempt.current === routeLocationId) return;
    locationFetchAttempt.current = routeLocationId;
    if (!loadedLocations[routeLocationId] && !loadingLocations[routeLocationId]) {
      fetchLocationDetails(routeTenantId, routeLocationId);
    }
    if (!managerShifts[routeLocationId]) {
      fetchManagerShifts(routeLocationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeTenantId, routeLocationId, routeInScope, tenants, loadedTenants]);

  // feat-308 — the Crew table's role_key → display_name lookup, per tenant.
  // Deliberately INDEPENDENT of the modal's tenantRoles/tenantRolesStatus
  // (feat-301 design R9 semantics live there). Fetched once per tenant id when
  // a location pane for that tenant is shown; a failure is silent and leaves
  // the table on raw keys (spec A3). A failed tenant is retried on a later
  // navigation, not on every render.
  const [crewRoleNames, setCrewRoleNames] = useState<Record<string, RoleNameLookup>>({});
  const crewRoleNamesAttempted = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!routeTenantId || !routeLocationId || !routeInScope) return;
    const tenantId = routeTenantId;
    if (crewRoleNamesAttempted.current.has(tenantId)) return;
    crewRoleNamesAttempted.current.add(tenantId);
    let authHeader: Record<string, string>;
    try {
      authHeader = getAuthHeaders();
    } catch {
      crewRoleNamesAttempted.current.delete(tenantId);
      return;
    }
    fetchApi<TenantRole[]>(`/api/tenant/${tenantId}/roles`, { headers: authHeader })
      .then((roles) => {
        const lookup: Record<string, string> = {};
        (Array.isArray(roles) ? roles : []).forEach((r) => {
          if (r && typeof r.role_key === "string" && typeof r.display_name === "string") {
            lookup[r.role_key] = r.display_name;
          }
        });
        setCrewRoleNames((prev) => ({ ...prev, [tenantId]: lookup }));
      })
      .catch(() => {
        crewRoleNamesAttempted.current.delete(tenantId);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeTenantId, routeLocationId, routeInScope]);

  // Fetch tenant roles when server modal opens
  useEffect(() => {
    if (modalType === 'server' && selectedTenant) {
      let cancelled = false;
      setTenantRolesStatus("loading");
      const authHeader = getAuthHeaders();
      fetchApi<TenantRole[]>(`/api/tenant/${selectedTenant}/roles`, { headers: authHeader })
        .then((roles) => {
          if (cancelled) return;
          setTenantRoles(Array.isArray(roles) ? roles : []);
          setTenantRolesStatus("loaded");
        })
        .catch(() => {
          // feat-301 (design R9): a failed fetch is NOT "tenant has no roles".
          // 'error' renders the server's current roles read-only and makes
          // rolesPayload omit both roles and default_role, so a save can never
          // wipe the server's roles.
          if (cancelled) return;
          setTenantRoles([]);
          setTenantRolesStatus("error");
        });
      return () => {
        cancelled = true;
      };
    }
  }, [modalType, selectedTenant]);

  // ---------------------------------------------------------------------------
  // feat-307 — modal openers. Each body is the inline onClick develop had on
  // the matching button, moved verbatim so the tree and both panes can share it.

  const openAddLocation = (tenantId: string) => {
    setSelectedTenant(tenantId);
    setEditingLocationId(null);
    setNewLocation({ name: "", timezone: "", auth_method: "phone_otp", recording_start_date: "", bespoke_go_live_date: "" });
    setModalType('location');
  };

  const openEditLocation = (tenant: Tenant, location: Location) => {
    setSelectedTenant(tenant.tenantId);
    setEditingLocationId(location.id);
    setNewLocation({ name: location.name, timezone: location.timezone, auth_method: location.authMethod || 'phone_otp', recording_start_date: location.recordingStartDate || '', bespoke_go_live_date: location.bespokeGoLiveDate || '' });
    // feat-303 — open on the SAVED override. Rate prefill: the
    // location's stored rate, otherwise the tenant's.
    setLocationDripForm({
      choice: locationDripChoice(location),
      rate: String(location.dripLessonsPerDay ?? tenant.dripLessonsPerDay ?? 1),
      invalid: false,
    });
    setModalType('location');
  };

  const openAddServer = (tenantId: string, locationId: string) => {
    setSelectedTenant(tenantId);
    setSelectedLocation(locationId);
    // feat-301 QA: a create always starts blank — never a
    // cancelled Edit's fields/roles, never a stale edit id.
    setEditingServerId(null);
    setNewServer(emptyNewServer());
    setError(null); // feat-155 fast-follow §2: no stale error in a freshly opened dialog
    setLearnCompleteNotice(null); // feat-255: nor a stale success line
    setModalType('server');
  };

  const openEditServer = (tenantId: string, locationId: string, server: Server) => {
    setSelectedTenant(tenantId);
    setSelectedLocation(locationId);
    setEditingServerId(server.id);
    setNewServer({
      first_name: server.firstName,
      last_name: server.lastName,
      phone: server.phone || '',
      pin: server.pin || '',
      default_role: server.defaultRole || '',
      roles: server.roles ?? serverRolesFromApi(undefined, server.defaultRole),
      notifications: server.features?.notifications || false,
      demo: server.demo || false,
      test: server.test || false,
      preferred_locale: server.preferredLocale || 'en',
      location_id: server.locationId || '',
      is_multi_location: server.isMultiLocation || false
    });
    setError(null); // feat-155 fast-follow §2: no stale error in a freshly opened dialog
    setLearnCompleteNotice(null); // feat-255: nor a stale success line
    setModalType('server');
  };

  const openAddManager = (tenantId: string, locationId: string | null, role: 'Corporate' | 'Manager') => {
    setSelectedTenant(tenantId);
    setSelectedLocation(locationId);
    setManagerError(null);
    setManagerModalRole(role);
    setModalType('manager');
  };

  const openAddManagerShift = (tenantId: string, locationId: string) => {
    setSelectedTenant(tenantId);
    setSelectedLocation(locationId);
    setEditingManagerShiftId(null);
    setNewManagerShift({ manager_id: '', server_ids: [], shift_type: 'Lunch', start_time: '', end_time: '', notes: '' });
    setModalType('manager-shift');
  };

  const openEditManagerShift = (tenantId: string, locationId: string, ms: ManagerShiftRecord) => {
    // Convert "YYYY-MM-DD HH:MM:SS" to "YYYY-MM-DDTHH:MM" for datetime-local input
    const toLocalInput = (dt: string) => dt ? dt.replace(' ', 'T').slice(0, 16) : '';
    setEditingManagerShiftId(ms.id);
    setNewManagerShift({
      manager_id: ms.manager_id,
      server_ids: ms.server_ids,
      shift_type: ms.shift_type || 'Lunch',
      start_time: toLocalInput(ms.start_time),
      end_time: toLocalInput(ms.end_time || ''),
      notes: ms.notes || ''
    });
    setSelectedTenant(tenantId);
    setSelectedLocation(locationId);
    setModalType('manager-shift');
  };

  // Fetch manager shifts for a location
  const fetchManagerShifts = async (locationId: string) => {
    try {
      const authHeader = getAuthHeaders();
      const response = await fetchApi<any>(`/api/management/manager-shifts?location_id=${locationId}`, {
        headers: authHeader,
      });
      setManagerShifts(prev => ({
        ...prev,
        [locationId]: response.manager_shifts || []
      }));
    } catch (err) {
      console.error('Error fetching manager shifts:', err);
      setManagerShifts(prev => ({ ...prev, [locationId]: [] }));
    }
  };

  // Create or update a manager shift
  const handleSaveManagerShift = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newManagerShift.manager_id || newManagerShift.server_ids.length === 0 || !newManagerShift.start_time || !newManagerShift.end_time) {
      setError("Manager, at least one server, start time, and end time are required");
      return;
    }

    setIsSubmitting(true);
    try {
      const authHeader = getAuthHeaders();
      const body = {
        manager_id: newManagerShift.manager_id,
        server_ids: newManagerShift.server_ids,
        shift_type: newManagerShift.shift_type,
        start_time: newManagerShift.start_time.replace('T', ' ') + ':00',
        end_time: newManagerShift.end_time.replace('T', ' ') + ':00',
        notes: newManagerShift.notes || null
      };

      if (editingManagerShiftId) {
        await fetchApi(`/api/management/manager-shifts/${editingManagerShiftId}`, {
          method: 'PUT',
          headers: authHeader,
          body
        });
      } else {
        await fetchApi('/api/management/manager-shifts', {
          method: 'POST',
          headers: authHeader,
          body
        });
      }

      // Reset form and close modal
      setNewManagerShift({ manager_id: '', server_ids: [], shift_type: 'Lunch', start_time: '', end_time: '', notes: '' });
      setEditingManagerShiftId(null);
      setModalType(null);
      setError(null);

      // Refresh the manager shifts for this location, and clear cached server shifts for edited record
      if (selectedLocation) {
        if (editingManagerShiftId) {
          setServerShiftsForManager(prev => {
            const next = { ...prev };
            delete next[editingManagerShiftId];
            return next;
          });
        }
        await fetchManagerShifts(selectedLocation);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${editingManagerShiftId ? 'update' : 'create'} manager shift`);
      console.error("Error saving manager shift:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete a manager shift
  const handleDeleteManagerShift = async (shiftId: string, locationId: string) => {
    if (!window.confirm('Are you sure you want to delete this manager shift?')) return;

    try {
      const authHeader = getAuthHeaders();
      await fetchApi(`/api/management/manager-shifts/${shiftId}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      await fetchManagerShifts(locationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete manager shift");
      console.error("Error deleting manager shift:", err);
    }
  };

  // Toggle server shifts accordion for a manager shift
  const toggleManagerShiftExpansion = async (ms: ManagerShiftRecord, locationId: string) => {
    const isExpanded = expandedManagerShifts[ms.id];
    setExpandedManagerShifts(prev => ({ ...prev, [ms.id]: !isExpanded }));

    // Fetch server shifts if expanding and not already cached
    if (!isExpanded && !serverShiftsForManager[ms.id]) {
      setServerShiftsForManager(prev => ({ ...prev, [ms.id]: { loading: true, shifts: [] } }));
      try {
        const authHeader = getAuthHeaders();
        const response = await fetchApi<any>('/api/management/server-shifts', {
          method: 'POST',
          headers: authHeader,
          body: {
            manager_shift_id: ms.id,
            manager_created: ms.start_time,
            manager_ended: ms.end_time,
            location_id: locationId
          }
        });
        setServerShiftsForManager(prev => ({
          ...prev,
          [ms.id]: { loading: false, shifts: response.server_shifts || [] }
        }));
      } catch (err) {
        console.error('Error fetching server shifts for manager shift:', err);
        setServerShiftsForManager(prev => ({
          ...prev,
          [ms.id]: { loading: false, shifts: [] }
        }));
      }
    }
  };

  // Handle tenant creation
  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fallbackPermissions.canCreateTenant) {
      setError("You don't have permission to create tenants");
      return;
    }

    if (!newTenant.tenant_name.trim()) {
      setError("Tenant name is required");
      return;
    }

    setManagerError(null);
    setIsSubmitting(true);
    try {
      const authHeader = getAuthHeaders();
      await fetchApi("/api/tenant", {
        method: "POST",
        headers: authHeader,
        body: newTenant,
      });

      setNewTenant({ tenant_name: "", description: "" });
      setNewTenantOpen(false);
      await fetchTenantsList();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create tenant");
      console.error("Error creating tenant:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle location creation
  const handleCreateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenant || !fallbackPermissions.canCreateLocation(selectedTenant)) {
      setError("You don't have permission to create locations for this tenant");
      return;
    }

    if (!newLocation.name.trim()) {
      setError("Location name is required");
      return;
    }

    if (!newLocation.timezone) {
      setError("Timezone is required");
      return;
    }

    setIsSubmitting(true);
    const tenantId = selectedTenant;
    try {
      const authHeader = getAuthHeaders();
      await fetchApi(`/api/tenant/${tenantId}/locations`, {
        method: "POST",
        headers: authHeader,
        body: newLocation,
      });

      setNewLocation({ name: "", timezone: "", auth_method: "phone_otp", recording_start_date: "", bespoke_go_live_date: "" });
      setSelectedTenant(null);
      setModalType(null);
      if (tenantId) await fetchTenantDetails(tenantId);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create location");
      console.error("Error creating location:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle location update
  const handleUpdateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenant || !editingLocationId || !fallbackPermissions.canEditLocation(selectedTenant, editingLocationId)) {
      setError("You don't have permission to edit this location");
      return;
    }

    if (!newLocation.name.trim()) {
      setError("Location name is required");
      return;
    }

    if (!newLocation.timezone) {
      setError("Timezone is required");
      return;
    }

    // feat-094: build the PUT body. The go-live cutoff is Admin-only-editable
    // (RBAC resolution doc 0002). Only an Admin's value is included, and only
    // when it changed vs. the saved value — so omitting it leaves the column
    // untouched, while an explicit `null` clears it ("Not yet live").
    const body: LocationUpdateRequest = {
      name: newLocation.name,
      timezone: newLocation.timezone,
      auth_method: newLocation.auth_method,
    };
    // feat-303 — drip override, validated BEFORE any confirm so an invalid rate
    // never costs the admin a go-live prompt first. Admin only, FAIL-CLOSED
    // (a non-admin body carrying drip_* — even null — is a 403 that would break
    // an unrelated rename), and only when it changes what is stored.
    const editingLoc = tenants
      .flatMap((t) => t.locations || [])
      .find((l) => l.id === editingLocationId);
    const editingTenant = tenants.find((t) => t.tenantId === selectedTenant);
    let dripBody: LocationDripBody | null = null;
    if (isAdminForLiveClient && editingLoc) {
      let rate: number | null = null;
      if (locationDripForm.choice === 'on') {
        rate = parseDripRate(locationDripForm.rate);
        if (rate === null) {
          setLocationDripForm((f) => ({ ...f, invalid: true }));
          locationDripRateRef.current?.focus();
          return;
        }
      }
      const candidate = locationDripBody(locationDripForm.choice, rate);
      if (locationDripChanged(editingLoc, candidate)) dripBody = candidate;
    }

    if (isAdmin) {
      // feat-320: recording start date — same diff-before-PUT and clear-to-NULL
      // contract as the go-live date, but no confirm (design NOTES §4). A no-op
      // save leaves the key out of the body entirely.
      const savedRecording = editingLoc?.recordingStartDate || '';
      const formRecording = newLocation.recording_start_date || '';
      if (formRecording !== savedRecording) {
        body.recording_start_date = formRecording || null;
      }

      const savedGoLive =
        tenants
          .flatMap((t) => t.locations || [])
          .find((l) => l.id === editingLocationId)?.bespokeGoLiveDate || '';
      const formGoLive = newLocation.bespoke_go_live_date || '';
      if (formGoLive !== savedGoLive) {
        // Soft validation (product decision #4): warn, never hard-block.
        if (formGoLive) {
          const today = new Date().toISOString().slice(0, 10);
          const created = (tenants
            .flatMap((t) => t.locations || [])
            .find((l) => l.id === editingLocationId)?.createdAt || '').slice(0, 10);
          if (formGoLive > today) {
            if (!window.confirm(
              `Bespoke training go-live date ${formGoLive} is in the future. This is allowed for a planned training start — save anyway?`
            )) {
              return;
            }
          } else if (created && formGoLive < created) {
            if (!window.confirm(
              `Bespoke training go-live date ${formGoLive} is before this location was created (${created}). Save anyway?`
            )) {
              return;
            }
          }
        }
        // Explicit null clears it; an ISO string sets it (contract A clear-to-NULL).
        body.bespoke_go_live_date = formGoLive ? formGoLive : null;
      }
    }

    if (dripBody && editingLoc) {
      // L7 — confirm when the EFFECTIVE value goes on → off (an explicit Off,
      // or an Inherit that resolves to a tenant that is off).
      const tenantDrip = editingTenant ?? {};
      const wasOn = effectiveLocationDrip(editingLoc, tenantDrip).enabled;
      const willBeOn = effectiveLocationDrip(
        { dripEnabled: dripBody.drip_enabled, dripLessonsPerDay: dripBody.drip_lessons_per_day },
        tenantDrip,
      ).enabled;
      if (wasOn && !willBeOn && !window.confirm(dripLocationTurnOffConfirm(editingLoc.name))) {
        return;
      }
      body.drip_enabled = dripBody.drip_enabled;
      body.drip_lessons_per_day = dripBody.drip_lessons_per_day;
    }

    setIsSubmitting(true);
    const tenantId = selectedTenant;
    try {
      const authHeader = getAuthHeaders();
      await fetchApi(`/api/tenant/${tenantId}/locations/${editingLocationId}`, {
        method: "PUT",
        headers: authHeader,
        body,
      });

      setNewLocation({ name: "", timezone: "", auth_method: "phone_otp", recording_start_date: "", bespoke_go_live_date: "" });
      setSelectedTenant(null);
      setEditingLocationId(null);
      setModalType(null);
      if (tenantId) await fetchTenantDetails(tenantId);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update location");
      console.error("Error updating location:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle server creation
  const handleCreateServer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenant || !selectedLocation || !fallbackPermissions.canCreateServer(selectedTenant, selectedLocation)) {
      setError("You don't have permission to create servers for this location");
      return;
    }

    if (!newServer.first_name.trim() && !newServer.last_name.trim()) {
      setError("At least a first name or last name is required");
      return;
    }

    if (!newServer.phone.trim() && !newServer.pin.trim()) {
      setError("At least a phone number or PIN is required");
      return;
    }

    if (newServer.phone.trim() && !isValidPhone(newServer.phone)) {
      setError("Please enter a valid 10-digit US phone number");
      return;
    }

    // Check if server name is unique within the location
    if (!isServerNameUnique(selectedTenant, selectedLocation, newServer.first_name, newServer.last_name)) {
      setError("A server with this name already exists in this location. Server names must be unique per location.");
      return;
    }

    setIsSubmitting(true);
    const tenantId = selectedTenant;
    const locationId = selectedLocation;
    try {
      const authHeader = getAuthHeaders();
      await fetchApi(`/api/tenant/${tenantId}/locations/${locationId}/servers`, {
        method: "POST",
        headers: authHeader,
        body: {
          first_name: newServer.first_name,
          last_name: newServer.last_name,
          phone: newServer.phone,
          pin: newServer.pin,
          // feat-301 (ADR 0001 §6.1): roles primary-first + default_role, or
          // neither when the tenant roles did not load (never `roles: []`).
          ...rolesPayload(
            { roles: newServer.roles, primary: newServer.default_role },
            tenantRolesStatus,
            tenantRoles.map((r) => r.role_key),
          ),
          features: { notifications: newServer.notifications },
          demo: newServer.demo,
        },
      });

      setNewServer(emptyNewServer());
      setSelectedTenant(null);
      setSelectedLocation(null);
      setModalType(null);
      if (tenantId && locationId) await fetchLocationDetails(tenantId, locationId);
      setError(null);
    } catch (err) {
      setError(serverSaveErrorMessage(err, "Failed to create server"));
      console.error("Error creating server:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle server update
  const handleUpdateServer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenant || !selectedLocation || !editingServerId) {
      setError("Missing server information for update");
      return;
    }

    if (!newServer.first_name.trim() && !newServer.last_name.trim()) {
      setError("At least a first name or last name is required");
      return;
    }

    if (!newServer.phone.trim() && !newServer.pin.trim()) {
      setError("At least a phone number or PIN is required");
      return;
    }

    if (newServer.phone.trim() && !isValidPhone(newServer.phone)) {
      setError("Please enter a valid 10-digit US phone number");
      return;
    }

    // Check uniqueness excluding the server being edited
    const fullName = `${newServer.first_name.trim()} ${newServer.last_name.trim()}`;
    const tenant = tenants.find(t => t.tenantId === selectedTenant);
    const location = tenant?.locations.find(l => l.id === selectedLocation);
    if (location) {
      const duplicate = location.servers.some(s =>
        s.id !== editingServerId && s.name.toLowerCase() === fullName.toLowerCase()
      );
      if (duplicate) {
        setError("A server with this name already exists in this location.");
        return;
      }
    }

    setIsSubmitting(true);
    const tenantId = selectedTenant;
    const locationId = selectedLocation;
    try {
      const authHeader = getAuthHeaders();
      await fetchApi(`/api/tenant/${tenantId}/locations/${locationId}/servers/${editingServerId}`, {
        method: "PUT",
        headers: authHeader,
        body: {
          first_name: newServer.first_name,
          last_name: newServer.last_name,
          phone: newServer.phone,
          pin: newServer.pin,
          // feat-301 (ADR 0001 §6.1): roles primary-first + default_role, or
          // neither when the tenant roles did not load (never `roles: []`).
          ...rolesPayload(
            { roles: newServer.roles, primary: newServer.default_role },
            tenantRolesStatus,
            tenantRoles.map((r) => r.role_key),
          ),
          features: { notifications: newServer.notifications },
          demo: newServer.demo,
          test: newServer.test,
          preferred_locale: newServer.preferred_locale,
          // feat-155 (ADR 0001 D4): reassign home location + toggle multi-location.
          // Backend fail-closes 404 if location_id is outside the caller's tenant.
          location_id: newServer.location_id,
          is_multi_location: newServer.is_multi_location,
        },
      });

      setNewServer(emptyNewServer());
      setSelectedTenant(null);
      setSelectedLocation(null);
      setEditingServerId(null);
      setModalType(null);
      if (tenantId && locationId) await fetchLocationDetails(tenantId, locationId);
      setError(null);
    } catch (err) {
      setError(serverSaveErrorMessage(err, "Failed to update server"));
      console.error("Error updating server:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * feat-196 (B2) — flip a tenant's CLIENT STATUS (`tenant.is_live`).
   *
   * Sends a TARGETED PUT carrying `is_live` and nothing else, modelled on
   * handleResetServerFlag below. There is no tenant edit form on this screen (the
   * dashboard does create + delete only, and no `PUT /api/tenant/{id}` existed in
   * this app before feat-196), so this control IS the write path — hence a
   * targeted PUT rather than a field on a form submit.
   *
   * Two properties are load-bearing, not stylistic:
   *
   * 1. **Only `is_live` is in the body.** The backend gates it on
   *    `tenant_data.is_live is not None` (ADR 0001 §3.4), so a body that always
   *    carried the key would defeat that sentinel. Keeping the body narrow also
   *    means this control can never clobber `tenant_name` / `description`.
   *
   * 2. **State comes from the SERVER after the write, never optimistically.**
   *    Preston removed the migration seed (there is no `110`), so this checkbox is
   *    the ONLY in-product way to make any tenant live — until it is clicked, every
   *    Interaction Evaluations page is empty for everyone by design. A checkbox that
   *    stays ticked because we set local state would be indistinguishable from a
   *    write that was dropped. So: await the PUT, then re-read the tenant list and
   *    let `isLive` re-render from `GET /api/tenant`. If the value comes back
   *    unchanged, the control visibly reverts — which is the signal that the write
   *    did not take.
   *
   * A non-Admin reaching this (they cannot: the control is gated fail-closed, and
   * the backend 403s regardless) surfaces the error inline rather than silently.
   */
  const handleToggleTenantLive = async (tenantId: string, nextIsLive: boolean) => {
    const tenant = tenants.find((t) => t.tenantId === tenantId);
    // Send ONLY when the value actually changed (mirrors feat-094's
    // diff-before-PUT for bespoke_go_live_date). A no-op click costs no request
    // and, more importantly, never puts `is_live` on the wire unnecessarily.
    if (tenant && !!tenant.isLive === nextIsLive) return;

    setLiveClientPendingTenantId(tenantId);
    setLiveClientError(null);
    try {
      const authHeader = getAuthHeaders();
      const body: TenantUpdateRequest = { is_live: nextIsLive };
      await fetchApi(`/api/tenant/${tenantId}`, {
        method: "PUT",
        headers: authHeader,
        body,
      });
      // Read-back (see 2 above): re-fetch rather than trusting the write.
      await fetchTenantsList();
    } catch (err) {
      setLiveClientError(
        err instanceof Error ? err.message : "Failed to update live-client status",
      );
      console.error("Error updating tenant live-client status:", err);
    } finally {
      setLiveClientPendingTenantId(null);
    }
  };

  /**
   * feat-303-curriculum-drip-release — write ONE tenant drip field.
   *
   * Same shape as handleToggleTenantLive, for the same reasons: the body carries
   * only the key that changed (the backend's admin gate is `is not None`, so an
   * always-present key would put drip on the wire for every save), and the UI
   * re-renders from a re-fetch, never optimistically. Turning drip OFF asks
   * first (T6, DESIGN-QUESTION.md Q1); cancelling sends nothing.
   */
  const handleTenantDripChange = async (
    tenantId: string,
    patch: { drip_enabled: boolean } | { drip_lessons_per_day: number },
  ) => {
    const tenant = tenants.find((t) => t.tenantId === tenantId);
    if ('drip_enabled' in patch) {
      if (tenant && !!tenant.dripEnabled === patch.drip_enabled) return;
      if (!patch.drip_enabled && !window.confirm(dripTenantTurnOffConfirm(tenant?.tenantName ?? 'this tenant'))) {
        return;
      }
    } else if (tenant && tenant.dripLessonsPerDay === patch.drip_lessons_per_day) {
      return;
    }

    setDripPendingTenantId(tenantId);
    setDripError(null);
    try {
      const body: TenantUpdateRequest = { ...patch };
      await fetchApi(`/api/tenant/${tenantId}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body,
      });
      await fetchTenantsList();
    } catch (err) {
      setDripError({ tenantId, message: dripSaveFailed(err instanceof Error ? err.message : null) });
      console.error("Error updating tenant drip release:", err);
    } finally {
      setDripPendingTenantId(null);
    }
  };

  /**
   * feat-305-chat-machinery-server-hist — flip a tenant's Manager assistant
   * (`tenant.assistant_enabled`).
   *
   * Same shape as handleToggleTenantLive / handleTenantDripChange: the body
   * carries ONLY `assistant_enabled` (the backend's admin gate is `is not None`),
   * the UI re-renders from a re-fetch, never optimistically. No confirm dialog —
   * turning it off is harmless (spec Addendum). On success the frontend
   * assistant config cache for that tenant is cleared, so the Server Performance
   * History launcher reflects the change without a reload.
   */
  const handleTenantAssistantChange = async (tenantId: string, nextEnabled: boolean) => {
    const tenant = tenants.find((t) => t.tenantId === tenantId);
    if (tenant && !!tenant.assistantEnabled === nextEnabled) return;

    setAssistantPendingTenantId(tenantId);
    setAssistantError(null);
    try {
      const body: TenantUpdateRequest = { assistant_enabled: nextEnabled };
      await fetchApi(`/api/tenant/${tenantId}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body,
      });
      invalidateAssistantConfig(tenantId);
      await fetchTenantsList();
    } catch (err) {
      setAssistantError({
        tenantId,
        message: assistantToggleSaveFailed(err instanceof Error ? err.message : null),
      });
      console.error("Error updating tenant manager assistant:", err);
    } finally {
      setAssistantPendingTenantId(null);
    }
  };

  /**
   * feat-354-auto-copy-lessons — flip a tenant's daily lesson auto-copy
   * (`tenant.learn_auto_copy_enabled`, CONTRACT C1b).
   *
   * Cloned from handleTenantAssistantChange: the body carries ONLY
   * `learn_auto_copy_enabled`, alone in its own PUT (a Corporate save carrying
   * it is a 403 for the whole request), and the UI re-renders from a re-fetch,
   * never optimistically. No confirm dialog (DESIGN-QUESTION Q7).
   */
  const handleTenantAutoCopyChange = async (tenantId: string, nextEnabled: boolean) => {
    const tenant = tenants.find((t) => t.tenantId === tenantId);
    if (tenant && !!tenant.learnAutoCopyEnabled === nextEnabled) return;

    setAutoCopyPendingTenantId(tenantId);
    setAutoCopyError(null);
    try {
      const body: TenantUpdateRequest = { learn_auto_copy_enabled: nextEnabled };
      await fetchApi(`/api/tenant/${tenantId}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body,
      });
      await fetchTenantsList();
    } catch (err) {
      setAutoCopyError({
        tenantId,
        message: autoCopyToggleSaveFailed(err instanceof Error ? err.message : null),
      });
      console.error("Error updating tenant lesson auto-copy:", err);
    } finally {
      setAutoCopyPendingTenantId(null);
    }
  };

  // Reset the training_complete or onboarding_complete flag on a server.
  // Sends a targeted PUT (doesn't go through the form submit) so the user gets
  // immediate feedback without having to also hit Save Changes.
  const handleResetServerFlag = async (
    tenantId: string,
    locationId: string,
    serverId: string,
    flag: 'training_complete' | 'onboarding_complete'
  ) => {
    const confirmMsg = flag === 'training_complete'
      ? 'Reset this server\'s training progress? They will be prompted to complete training again on next login.'
      : 'Reset this server\'s onboarding progress? They will be prompted through the onboarding flow again on next login.';
    if (!window.confirm(confirmMsg)) return;

    setIsSubmitting(true);
    try {
      const authHeader = getAuthHeaders();
      await fetchApi(`/api/tenant/${tenantId}/locations/${locationId}/servers/${serverId}`, {
        method: "PUT",
        headers: authHeader,
        body: { [flag]: false },
      });
      await fetchLocationDetails(tenantId, locationId);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to reset ${flag}`);
      console.error(`Error resetting ${flag}:`, err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Wipe the server's Learn-module state — quiz attempts, per-question
  // history, quiz XP credits, and quiz-driven badges. Used to prep demo
  // accounts so the same walkthrough plays cleanly each time.
  const handleResetLearnProgress = async (
    tenantId: string,
    locationId: string,
    serverId: string,
  ) => {
    if (!window.confirm(
      'Reset this server\'s Learn module progress? All quiz attempts, per-question history, mastery records, lesson completions, rehearsal attempts, quiz + rehearsal XP, and lesson-based badges will be deleted. This cannot be undone.'
    )) return;

    setIsSubmitting(true);
    try {
      const authHeader = getAuthHeaders();
      await fetchApi(
        `/api/tenant/${tenantId}/locations/${locationId}/servers/${serverId}/reset-learn-progress`,
        { method: "POST", headers: authHeader },
      );
      await fetchLocationDetails(tenantId, locationId);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset Learn progress");
      console.error("Error resetting Learn progress:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // feat-255-pass-servers-thru-content: the inverse of the Reset above — mark
  // every active drill in this tenant as passed for a TEST server, so a Bespoke
  // employee can jump straight to newly authored Learn content instead of
  // grinding the whole curriculum.
  //
  // Deliberately NO window.confirm (unlike handleResetLearnProgress directly
  // above): the write is additive, idempotent, and completely undone by that
  // Reset button, which sits one row up in the same Progress section.
  //
  // The button that calls this is gated on the PERSISTED `currentServer.test`,
  // not the unsaved `newServer.test` toggle, so the UI's enabled state and the
  // backend's test-server 403 can never disagree. The backend gate is the real
  // one; this handler is not load-bearing for authorization.
  const handleCompleteLearnProgress = async (
    tenantId: string,
    locationId: string,
    serverId: string,
  ) => {
    setIsSubmitting(true);
    setLearnCompleteNotice(null);
    try {
      const authHeader = getAuthHeaders();
      const result = await fetchApi<CompleteLearnProgressResponse>(
        `/api/tenant/${tenantId}/locations/${locationId}/servers/${serverId}/complete-learn-progress`,
        { method: "POST", headers: authHeader },
      );
      await fetchLocationDetails(tenantId, locationId);
      setError(null);
      setLearnCompleteNotice(buildCompleteLearnNotice(result));
    } catch (err) {
      setLearnCompleteNotice(null);
      setError(err instanceof Error ? err.message : "Failed to mark curriculum complete");
      console.error("Error marking curriculum complete:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // feat-022-consent-evidence: fetch the location's consent-evidence payload
  // and build a downloadable PDF (ADR 0001 §8/§9). The report endpoint is the
  // source of truth — generated_by/generated_at are stamped server-side.
  const handleGenerateConsentReport = async (tenantId: string, locationId: string) => {
    setGeneratingReportLocationId(locationId);
    setError(null);
    try {
      const report = await fetchConsentReport(tenantId, locationId, getAuthHeaders());
      await generateConsentEvidencePdf(report);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate consent evidence report');
      console.error('Error generating consent evidence report:', err);
    } finally {
      setGeneratingReportLocationId(null);
    }
  };

  // feat-022-consent-evidence: withdraw a server's consent (ADR 0001 §6). The
  // backend atomically clears consent, resets onboarding, and appends the audit
  // events. `reason` is mandatory and captured by the withdraw modal.
  const handleWithdrawConsent = async () => {
    if (!withdrawTarget) return;
    const reason = withdrawReason.trim();
    if (!reason) return; // modal guards this; defensive.

    setIsSubmitting(true);
    try {
      await withdrawConsent(
        withdrawTarget.tenantId,
        withdrawTarget.locationId,
        withdrawTarget.serverId,
        reason,
        getAuthHeaders(),
      );
      await fetchLocationDetails(withdrawTarget.tenantId, withdrawTarget.locationId);
      setError(null);
      setWithdrawTarget(null);
      setWithdrawReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to withdraw consent');
      console.error('Error withdrawing consent:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenXpAudit = async (
    tenantId: string,
    locationId: string,
    serverId: string,
    serverName: string,
  ) => {
    setSelectedTenant(tenantId);
    setSelectedLocation(locationId);
    setAuditingServerId(serverId);
    setAuditingServerName(serverName);
    setAuditData(null);
    setAuditError(null);
    setAuditLoading(true);
    setModalType('server-audit');
    try {
      const authHeader = getAuthHeaders();
      const data = await fetchApi<typeof auditData>(
        `/api/tenant/${tenantId}/locations/${locationId}/servers/${serverId}/xp-audit`,
        { headers: authHeader },
      );
      setAuditData(data);
    } catch (err) {
      setAuditError(err instanceof Error ? err.message : "Failed to load XP audit");
      console.error("Error loading XP audit:", err);
    } finally {
      setAuditLoading(false);
    }
  };

  // Email validation function
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation function - 10-digit US phone number
  const isValidPhone = (phone: string): boolean => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    // Check if it's exactly 10 digits
    return digitsOnly.length === 10;
  };

  // Format phone number for display (e.g., "6502699246" -> "(650) 269-9246")
  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return 'N/A';
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX if 10 digits
    if (digitsOnly.length === 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    }
    // Return as-is if not 10 digits
    return phone;
  };

  // Check if server name is unique within a location
  const isServerNameUnique = (tenantId: string, locationId: string, firstName: string, lastName: string): boolean => {
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    
    // Find the tenant and location
    const tenant = tenants.find(t => t.tenantId === tenantId);
    if (!tenant) return true;
    
    const location = tenant.locations.find(l => l.id === locationId);
    if (!location) return true;
    
    // Check if any existing server has the same name
    return !location.servers.some(server => 
      server.name.toLowerCase() === fullName.toLowerCase()
    );
  };

  // Handle manager creation
  const handleCreateManager = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenant || !fallbackPermissions.canCreateManager(selectedTenant, selectedLocation || undefined)) {
      setError("You don't have permission to create managers");
      return;
    }

    if (!newManager.email.trim() || !newManager.first_name.trim() || !newManager.last_name.trim() || !newManager.phone.trim()) {
      setError("Email, first name, last name, and phone are required");
      return;
    }

    if (!isValidEmail(newManager.email)) {
      setManagerError("Please enter a valid email address");
      return;
    }

    if (!isValidPhone(newManager.phone)) {
      setManagerError("Please enter a valid 10-digit US phone number");
      return;
    }

    setIsSubmitting(true);
    const tenantId = selectedTenant;
    const locationId = selectedLocation;
    try {
      const authHeader = getAuthHeaders();
      const roleToCreate = managerModalRole;
      const endpoint = roleToCreate === 'Manager' && locationId
        ? `/api/tenant/${tenantId}/locations/${locationId}/managers`
        : `/api/tenant/${tenantId}/managers`;

      const response = await fetchApi<Manager>(endpoint, {
        method: "POST",
        headers: authHeader,
        body: { ...newManager, role: roleToCreate },
      });

      // Close the creation modal
      setNewManager({ email: "", first_name: "", last_name: "", phone: "", role: "Manager" });
      setSelectedTenant(null);
      setSelectedLocation(null);
      setModalType(null);
      setManagerModalRole('Manager');

      // Show password popup with manager details
      if (response.temporary_password) {
        setPasswordPopup({
          show: true,
          email: response.email,
          firstName: response.first_name || response.firstName,
          lastName: response.last_name || response.lastName,
          phone: response.phone,
          role: response.role,
          password: response.temporary_password
        });
      }

      if (tenantId) {
        if (roleToCreate === 'Manager' && locationId) {
          await fetchLocationDetails(tenantId, locationId);
        } else {
          await fetchTenantDetails(tenantId);
        }
      }
      setError(null);
    } catch (err) {
      // Handle specific error for email already exists
      if (err instanceof Error && err.message.includes("Email already exists")) {
        setManagerError("Email already exists in user pool or database");
      } else {
        setManagerError("Failed to create manager");
      }
      console.error("Error creating manager:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle deletion
  // Deactivate a manager: sets management.deactivated_at and disables Cognito.
  // Past shifts continue to render the manager's name on historical reports.
  // Reactivation is manual during the L&L pilot (founders on call).
  const handleDeactivateManager = async (
    managerId: string,
    firstName: string,
    lastName: string,
    email: string,
    lastManagerWarning?: { tenantName: string },
  ) => {
    const baseMsg =
      `Deactivate manager ${firstName} ${lastName} (${email})?\n\n` +
      `They will no longer be able to log in or appear in the manager list. ` +
      `Their past shifts and reports stay visible. ` +
      `Reactivation is manual — message Bespoke if this was a mistake.`;
    const warning = lastManagerWarning
      ? `\n\nWARNING: This is the last corporate manager for ${lastManagerWarning.tenantName}. ` +
        `No one will be able to manage this tenant until you add a new one.`
      : "";
    if (!window.confirm(baseMsg + warning)) return;

    setLoading(true);
    setError(null);
    try {
      const authHeader = getAuthHeaders();
      const result = await fetchApi<{ message: string; action: string }>(
        `/api/managers/${managerId}`,
        { method: "DELETE", headers: authHeader },
      );

      // Cognito disable failed but DB state is authoritative — surface the
      // operator-facing recovery message so they can finish manually.
      if (result.action === "deactivated_db_only") {
        setError(result.message);
      }

      // Same refresh sequence as handleDelete's 'manager' branch — manager
      // deletes don't carry tenant/location scope, so we drop nested caches
      // and re-fetch any currently-expanded subtrees.
      setLoadedTenants({});
      setLoadedLocations({});
      await fetchTenantsList();
      for (const [tId, expanded] of Object.entries(expandedTenants)) {
        if (expanded) await fetchTenantDetails(tId);
      }
      for (const [lId, expanded] of Object.entries(expandedLocations)) {
        if (!expanded) continue;
        const owner = tenants.find(t => t.locations.some(l => l.id === lId));
        if (owner) await fetchLocationDetails(owner.tenantId, lId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to deactivate manager");
      console.error("Error deactivating manager:", err);
    } finally {
      setLoading(false);
    }
  };

  // feat-321-display-shift-leads (design D3) — deactivate a shift lead. Same
  // DELETE /api/managers/{id} as a manager (admin-only for a shift_lead row,
  // feat-192 default-deny), but worded for a shift lead: they have no login,
  // and the consequence that matters is the exception-report email.
  // After success we re-fetch THIS location's details, which re-reads the
  // shift-leads list — the manager refresh sequence above only knows about
  // expanded tree nodes and would leave the row (and counts) stale.
  const handleDeactivateShiftLead = async (
    shiftLead: Manager,
    tenantId: string,
    locationId: string,
    locationName: string,
  ) => {
    const msg =
      `Deactivate shift lead ${shiftLead.firstName} ${shiftLead.lastName} (${shiftLead.email})?\n\n` +
      `They will stop receiving ${locationName}'s exception report and will no longer appear in the shift lead list. ` +
      `Reactivation is manual — message Bespoke if this was a mistake.`;
    if (!window.confirm(msg)) return;

    setLoading(true);
    setError(null);
    try {
      const authHeader = getAuthHeaders();
      const result = await fetchApi<{ message: string; action: string }>(
        `/api/managers/${shiftLead.id}`,
        { method: "DELETE", headers: authHeader },
      );
      if (result?.action === "deactivated_db_only") {
        setError(result.message);
      }
      await fetchLocationDetails(tenantId, locationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to deactivate shift lead");
      console.error("Error deactivating shift lead:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type: string, id: string, tenantId?: string, locationId?: string) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) {
      return;
    }

    setLoading(true);
    try {
      const authHeader = getAuthHeaders();
      let endpoint = "";
      
      switch (type) {
        case "tenant":
          endpoint = `/api/tenant/${id}`;
          break;
        case "location":
          endpoint = `/api/tenant/${tenantId}/locations/${id}`;
          break;
        case "server":
          endpoint = `/api/tenant/${tenantId}/locations/${locationId}/servers/${id}`;
          break;
        case "manager":
          endpoint = `/api/managers/${id}`;
          break;
      }

      await fetchApi(endpoint, {
        method: "DELETE",
        headers: authHeader,
      });

      // Targeted refresh based on what was deleted
      switch (type) {
        case "tenant":
          // Drop any cached nested data tied to this tenant
          setLoadedTenants(prev => { const next = { ...prev }; delete next[id]; return next; });
          setExpandedTenants(prev => { const next = { ...prev }; delete next[id]; return next; });
          await fetchTenantsList();
          break;
        case "location":
          setLoadedLocations(prev => { const next = { ...prev }; delete next[id]; return next; });
          setExpandedLocations(prev => { const next = { ...prev }; delete next[id]; return next; });
          if (tenantId) await fetchTenantDetails(tenantId);
          break;
        case "server":
          if (tenantId && locationId) await fetchLocationDetails(tenantId, locationId);
          break;
        case "manager":
          // Manager delete doesn't carry tenant/location scope; refresh the flat list
          // and mark all nested data stale so it re-fetches on next expand.
          setLoadedTenants({});
          setLoadedLocations({});
          await fetchTenantsList();
          // Re-fetch currently expanded subtrees
          for (const [tId, expanded] of Object.entries(expandedTenants)) {
            if (expanded) await fetchTenantDetails(tId);
          }
          for (const [lId, expanded] of Object.entries(expandedLocations)) {
            if (!expanded) continue;
            // Find which tenant owns this location
            const owner = tenants.find(t => t.locations.some(l => l.id === lId));
            if (owner) await fetchLocationDetails(owner.tenantId, lId);
          }
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to delete ${type}`);
      console.error(`Error deleting ${type}:`, err);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while auth is loading or while fetching tenants
  if (authLoading || (loading && tenants.length === 0)) {
    return (
      <div className="min-h-screen bg-cream py-16 px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-softBorder border-b-coral mx-auto"></div>
          <p className="mt-4 text-sm text-inkMuted">
            {authLoading ? 'Loading user data...' : 'Loading tenant data...'}
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // feat-307 — the sidebar-tree layout (spec D2). Only role-filtered data is
  // looked up, and only for an in-scope selection (D5).
  const visibleTenants = getFilteredTenants(tenants);
  const selectedTenantObj =
    routeTenantId && routeInScope
      ? visibleTenants.find((t) => t.tenantId === routeTenantId) ?? null
      : null;
  const selectedLocationObj =
    selectedTenantObj && routeLocationId
      ? selectedTenantObj.locations.find((l) => l.id === routeLocationId) ?? null
      : null;
  // D5: Admin and Corporate get the tree; a Manager's one-leaf tree is noise.
  const showTree = scopeRole === 'Admin' || scopeRole === 'Corporate';

  // T4 + T5 — the Admin-only client-status card for one tenant.
  const renderClientStatus = (tenant: Tenant) => (
    <>
      {/* feat-196 (B2 / D8) — the Live-client control. ADMIN ONLY, gated
          FAIL-CLOSED (security-01 G11): an unresolvable userContext hides
          it entirely. This is presentation; the backend 403 is the control.
          feat-303 — tenant drip release sits in the same card behind the SAME
          fail-closed gate (never `isAdmin`, which defaults to true). */}
      {isAdminForLiveClient && (
        <section className={`${cls.card} p-4`} data-testid={`client-status-${tenant.tenantId}`} aria-labelledby={`client-status-heading-${tenant.tenantId}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 id={`client-status-heading-${tenant.tenantId}`} className={`${cls.h2} !text-base`}>Client status</h3>
            <span className={cls.eyebrow}>Admin only</span>
          </div>
          <div className="flex flex-wrap items-start gap-x-10 gap-y-3">
            <div data-testid={`live-client-block-${tenant.tenantId}`}>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <span className="relative inline-flex items-center">
                    <input
                      type="checkbox"
                      data-testid={`live-client-checkbox-${tenant.tenantId}`}
                      checked={!!tenant.isLive}
                      disabled={liveClientPendingTenantId === tenant.tenantId}
                      onChange={(e) => handleToggleTenantLive(tenant.tenantId, e.target.checked)}
                      className="sr-only peer"
                    />
                    <span aria-hidden="true" className="w-9 h-5 rounded-full bg-cream-dark border border-softBorder peer-checked:bg-coral peer-checked:border-coral peer-disabled:opacity-50 peer-focus-visible:ring-2 peer-focus-visible:ring-coral/40 transition-colors" />
                    <span aria-hidden="true" className="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                  </span>
                  <span className="text-sm font-semibold text-ink">Live client</span>
                  {liveClientPendingTenantId === tenant.tenantId && (
                    <span className="text-xs text-inkMuted">Saving…</span>
                  )}
                </label>
                {/* D8 — Preston's wording, VERBATIM. The last sentence is
                    LOAD-BEARING: without it the natural reading of "live" is
                    "turn recording on", which this control does NOT do. Do not
                    trim it, and do not summarise it into the label.
                    feat-307 (D7): moved behind an ⓘ, not reworded. */}
                <InfoPopover label="What does Live client do?" testId={`live-client-help-${tenant.tenantId}`}>
                  <p>
                    <span className="font-semibold">Live client.</span> Turn this on once the
                    client is actually recording real customer interactions. Interactions from
                    clients that are <span className="font-semibold">not</span> marked live are
                    hidden from Interaction Evaluations and are{' '}
                    <span className="font-semibold">not</span> picked up by the nightly AI judge.
                    This does not affect recording, scorecards, or the real-time double-check —
                    those keep running for every client.
                  </p>
                </InfoPopover>
              </div>
              {liveClientError && liveClientPendingTenantId === null && (
                <p
                  role="alert"
                  data-testid={`live-client-error-${tenant.tenantId}`}
                  className="text-xs text-danger-text mt-1"
                >
                  {liveClientError}
                </p>
              )}
            </div>
            <TenantDripControl
              tenantId={tenant.tenantId}
              enabled={!!tenant.dripEnabled}
              rate={tenant.dripLessonsPerDay ?? 1}
              pending={dripPendingTenantId === tenant.tenantId}
              error={dripError?.tenantId === tenant.tenantId ? dripError.message : null}
              onToggle={(next) => handleTenantDripChange(tenant.tenantId, { drip_enabled: next })}
              onRateSave={(n) => handleTenantDripChange(tenant.tenantId, { drip_lessons_per_day: n })}
            />
            {/* feat-354-auto-copy-lessons — lesson auto-copy (design NOTES §3).
                Directly after Drip release, before Manager assistant; same card,
                same fail-closed `isAdminForLiveClient` gate, the assistant block's
                markup class for class. `align="right"` as Drip release does: a
                left-aligned 26rem panel runs past the card at this position. */}
            <div data-testid={`auto-copy-toggle-block-${tenant.tenantId}`}>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <span className="relative inline-flex items-center">
                    <input
                      type="checkbox"
                      data-testid={`auto-copy-toggle-checkbox-${tenant.tenantId}`}
                      checked={!!tenant.learnAutoCopyEnabled}
                      disabled={autoCopyPendingTenantId === tenant.tenantId}
                      onChange={(e) => handleTenantAutoCopyChange(tenant.tenantId, e.target.checked)}
                      className="sr-only peer"
                    />
                    <span aria-hidden="true" className="w-9 h-5 rounded-full bg-cream-dark border border-softBorder peer-checked:bg-coral peer-checked:border-coral peer-disabled:opacity-50 peer-focus-visible:ring-2 peer-focus-visible:ring-coral/40 transition-colors" />
                    <span aria-hidden="true" className="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                  </span>
                  <span className="text-sm font-semibold text-ink">{AUTO_COPY_TOGGLE_LABEL}</span>
                  {autoCopyPendingTenantId === tenant.tenantId && (
                    <span className="text-xs text-inkMuted">{AUTO_COPY_TOGGLE_SAVING}</span>
                  )}
                </label>
                <InfoPopover align="right" label={AUTO_COPY_TOGGLE_HELP_LABEL} testId={`auto-copy-toggle-help-${tenant.tenantId}`}>
                  <p>
                    <span className="font-semibold">{AUTO_COPY_TOGGLE_HELP_LEAD}</span>{' '}
                    {AUTO_COPY_TOGGLE_HELP_BODY}
                  </p>
                </InfoPopover>
              </div>
              {autoCopyError?.tenantId === tenant.tenantId && autoCopyPendingTenantId === null && (
                <p
                  role="alert"
                  data-testid={`auto-copy-toggle-error-${tenant.tenantId}`}
                  className="text-xs text-danger-text mt-1"
                >
                  {autoCopyError.message}
                </p>
              )}
            </div>
            {/* feat-305-chat-machinery-server-hist — Manager assistant. Same
                card, same fail-closed gate, same switch markup as Live client. */}
            <div data-testid={`assistant-toggle-block-${tenant.tenantId}`}>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <span className="relative inline-flex items-center">
                    <input
                      type="checkbox"
                      data-testid={`assistant-toggle-checkbox-${tenant.tenantId}`}
                      checked={!!tenant.assistantEnabled}
                      disabled={assistantPendingTenantId === tenant.tenantId}
                      onChange={(e) => handleTenantAssistantChange(tenant.tenantId, e.target.checked)}
                      className="sr-only peer"
                    />
                    <span aria-hidden="true" className="w-9 h-5 rounded-full bg-cream-dark border border-softBorder peer-checked:bg-coral peer-checked:border-coral peer-disabled:opacity-50 peer-focus-visible:ring-2 peer-focus-visible:ring-coral/40 transition-colors" />
                    <span aria-hidden="true" className="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                  </span>
                  <span className="text-sm font-semibold text-ink">{ASSISTANT_TOGGLE_LABEL}</span>
                  {assistantPendingTenantId === tenant.tenantId && (
                    <span className="text-xs text-inkMuted">{ASSISTANT_TOGGLE_SAVING}</span>
                  )}
                </label>
                <InfoPopover label={ASSISTANT_TOGGLE_HELP_LABEL} testId={`assistant-toggle-help-${tenant.tenantId}`}>
                  <p>
                    <span className="font-semibold">{ASSISTANT_TOGGLE_HELP_LEAD}</span>{' '}
                    {ASSISTANT_TOGGLE_HELP_BODY}
                  </p>
                </InfoPopover>
              </div>
              {assistantError?.tenantId === tenant.tenantId && assistantPendingTenantId === null && (
                <p
                  role="alert"
                  data-testid={`assistant-toggle-error-${tenant.tenantId}`}
                  className="text-xs text-danger-text mt-1"
                >
                  {assistantError.message}
                </p>
              )}
            </div>
          </div>
        </section>
      )}
    </>
  );

  // T11 → T12 → T13, in that order (load-bearing; see the comments).
  const renderSettingsCards = (tenant: Tenant) => (
    <section aria-label="Tenant settings" className="grid gap-x-4 lg:grid-cols-2 2xl:grid-cols-3 items-start">
                {/* Tenant default language (feat-037-spanish-onboarding, ADR 0003 Q4).
                    Admin/Corporate only — same gate as Add Location/Manager. */}
                <TenantLanguageDefaultCard
                  tenantId={tenant.tenantId}
                  initialLanguage={tenant.defaultLanguage}
                  canManage={
                    userContext?.role === 'Admin' || userContext?.role === 'Corporate'
                  }
                />
                {/* Tenant translation languages (feat-260, ADR 0001 §7.3/§7.4).

                    ORDER IS LOAD-BEARING: this card renders immediately AFTER the
                    default-language card, never before it. Their two eyebrow labels
                    read as a pair — "WHAT TRAINEES SEE" then "WHAT GETS TRANSLATED"
                    — and the contrast is what lets a reader tell which setting is
                    which (spec AT12). Reversing them inverts the reading order the
                    copy was written for.

                    UNCONDITIONAL, exactly as its sibling above is. `canManage` is
                    passed DOWN and disables the controls; it does not gate the
                    render. RULED 2026-08-25 (spec AT11, `design/states.md` §8) —
                    `StaffRoute` admits Manager, and a manager is precisely the
                    reader most likely to conflate the two settings, so hiding one
                    card from them defeats the pairing. Ruled 2026-08-25; ADR 0001
                    §7.4 was corrected at r3 and agrees. Do not "fix" this to hide. */}
                <TenantTranslationLanguagesCard
                  tenantId={tenant.tenantId}
                  canManage={
                    userContext?.role === 'Admin' || userContext?.role === 'Corporate'
                  }
                />
                {/* feat-271 — the switch for feat-270's curriculum approval
                    gate. feat-270 shipped the endpoint and the API client and
                    wired no control to either, so the feature could only be
                    enabled by a hand-rolled API call.

                    ⚠ `canManage` IS ADMIN-ONLY, AND NOT THE SIBLING'S GATE.
                    The card above is Admin-OR-Corporate because `update_tenant`
                    is; this endpoint is `require_admin`, because a corporate
                    manager able to turn the gate OFF could clear their own
                    pending changes by disabling the feature (ADR-B §3.5, D6).
                    Do not "make them consistent" — the asymmetry is deliberate.

                    ⚠ FAIL-CLOSED, so this is `userContext?.role === 'Admin'`
                    and NOT the `isAdmin` bound at :284, which defaults to TRUE
                    when the context cannot be resolved. Chaining that fail-OPEN
                    flag to a write is precisely feat-196's G11 finding; see the
                    comment on `isAdminForLiveClient`. */}
                <TenantCurriculumApprovalCard
                  tenantId={tenant.tenantId}
                  canManage={userContext?.role === 'Admin'}
                />
    </section>
  );

  let pane: React.ReactNode = null;
  if (selectedTenantObj && selectedLocationObj) {
    const tenant = selectedTenantObj;
    const location = selectedLocationObj;
    pane = (
      <LocationPane
        tenant={tenant}
        location={location}
        detailsLoaded={!!loadedLocations[location.id]}
        detailsLoading={!!loadingLocations[location.id]}
        showDripSummary={isAdminForLiveClient}
        canCreateServer={fallbackPermissions.canCreateServer(tenant.tenantId, location.id)}
        onAddServer={() => openAddServer(tenant.tenantId, location.id)}
        canCreateManager={fallbackPermissions.canCreateManager(tenant.tenantId, location.id)}
        onAddManager={() => openAddManager(tenant.tenantId, location.id, 'Manager')}
        canEditLocation={fallbackPermissions.canEditLocation(tenant.tenantId, location.id)}
        onEditLocation={() => openEditLocation(tenant, location)}
        generatingReport={generatingReportLocationId === location.id}
        onConsentEvidence={() => handleGenerateConsentReport(tenant.tenantId, location.id)}
        onEditServer={(server) => openEditServer(tenant.tenantId, location.id, server)}
        onXpHistory={(server) => handleOpenXpAudit(tenant.tenantId, location.id, server.id, server.name)}
        formatPhone={formatPhoneNumber}
        canDeleteManager={(id) => fallbackPermissions.canDeleteManager(id)}
        onDeactivateManager={(m) => handleDeactivateManager(m.id, m.firstName, m.lastName, m.email)}
        // feat-321: fail-CLOSED Admin gate (never canDeleteManager, which is
        // true for Corporate, nor the fail-open isAdmin) — the backend DELETE
        // is admin-only for a shift_lead row.
        canDeactivateShiftLead={isAdminForLiveClient}
        onDeactivateShiftLead={(lead) => handleDeactivateShiftLead(lead, tenant.tenantId, location.id, location.name)}
        managerShifts={managerShifts[location.id]}
        expandedManagerShifts={expandedManagerShifts}
        serverShiftsForManager={serverShiftsForManager}
        onToggleManagerShift={(ms) => toggleManagerShiftExpansion(ms, location.id)}
        onAddManagerShift={() => openAddManagerShift(tenant.tenantId, location.id)}
        onEditManagerShift={(ms) => openEditManagerShift(tenant.tenantId, location.id, ms)}
        onDeleteManagerShift={(ms) => handleDeleteManagerShift(ms.id, location.id)}
        roleNames={crewRoleNames[tenant.tenantId]}
      />
    );
  } else if (selectedTenantObj && routeLocationId) {
    // A location deep link whose tenant's locations are still loading.
    pane = <div className={`p-8 ${cls.muted}`} data-testid="pane-loading">Loading...</div>;
  } else if (selectedTenantObj) {
    const tenant = selectedTenantObj;
    pane = (
      <TenantPane
        tenant={tenant}
        detailsLoaded={!!loadedTenants[tenant.tenantId]}
        detailsLoading={!!loadingTenants[tenant.tenantId]}
        showClientStatus={isAdminForLiveClient}
        clientStatus={renderClientStatus(tenant)}
        settingsCards={renderSettingsCards(tenant)}
        canCreateLocation={fallbackPermissions.canCreateLocation(tenant.tenantId)}
        onAddLocation={() => openAddLocation(tenant.tenantId)}
        onPromptConfig={() => { window.location.href = `/prompt-management?tenantId=${tenant.tenantId}`; }}
        hasDemoJob={tenantIdsWithJobs.has(tenant.tenantId)}
        onViewDemoSetup={() => openDemoSetupViewer(tenant.tenantId, tenant.tenantName)}
        canCreateCorporateManager={fallbackPermissions.canCreateManager(tenant.tenantId)}
        onAddCorporateManager={() => openAddManager(tenant.tenantId, null, 'Corporate')}
        canDeleteManager={(id) => fallbackPermissions.canDeleteManager(id)}
        onDeactivateCorporateManager={(m) =>
          handleDeactivateManager(
            m.id,
            m.firstName,
            m.lastName,
            m.email,
            (tenant.corporateManagers?.length ?? 0) === 1
              ? { tenantName: tenant.tenantName }
              : undefined,
          )
        }
      />
    );
  } else if (!routeTenantId && scopeRole === 'Admin') {
    pane = <RootPane tenants={visibleTenants} showClientStatus={isAdminForLiveClient} />;
  }

  const rawRoleLabel = userContext?.role || user?.managementUser?.role;
  const scopeSentence =
    rawRoleLabel === 'Admin'
      ? "You can see all tenants, locations, and managers"
      : rawRoleLabel === 'Corporate'
        ? "You can see your tenant's locations and managers"
        : rawRoleLabel === 'Manager'
          ? "You can see your location's servers and managers"
          : null;

  return (
    <div
      className="min-h-screen bg-cream text-ink"
      data-route-tenant={routeTenantId ?? undefined}
      data-route-location={routeLocationId ?? undefined}
    >
      <ManagementHeader
        onBack={handleBackClick}
        onLogout={handleLogout}
        canCreateTenant={!!fallbackPermissions.canCreateTenant}
        onGenerateDemo={() => navigate("/tenant_management/demo-setup")}
        onNewTenant={() => setNewTenantOpen(true)}
        email={userContext?.email || user?.email || 'admin@test.com'}
        role={userContext?.role || user?.managementUser?.role || 'Unknown'}
        tenantId={userContext?.tenantId || user?.managementUser?.tenant_id}
        locationId={userContext?.locationId || user?.managementUser?.location_id}
        scopeSentence={scopeSentence}
        sidebarToggle={showTree ? { open: treeOpen, onToggle: () => setTreeOpen((o) => !o) } : null}
      />

      {/* Error alert (P4). No role="alert", as on develop: the server modal has its
          own inline alert for the same message, and two live regions would
          announce it twice. */}
      {error && (
        <div className="mx-4 lg:mx-6 mt-4 flex items-start gap-3 rounded-xl border border-danger-line/40 bg-danger-surface px-4 py-3 text-sm text-danger-text" data-testid="page-error-banner">
          <p className="flex-1 min-w-0 break-words">{error}</p>
          <button className="text-danger-text" onClick={() => setError(null)} title="Dismiss">
            ✕
          </button>
        </div>
      )}

      <div className="flex items-start">
        {showTree && (
          <>
            {treeOpen && (
              <div
                className="fixed inset-0 z-30 bg-black/30 lg:hidden"
                onClick={() => setTreeOpen(false)}
                aria-hidden="true"
              />
            )}
            <aside
              id="tenant-tree"
              aria-label="Tenants and locations"
              className={`${
                treeOpen ? 'fixed inset-y-0 left-0 z-40 flex w-[min(20rem,85vw)] shadow-xl' : 'hidden'
              } lg:flex lg:sticky lg:top-0 lg:z-auto lg:shadow-none lg:w-80 lg:h-screen lg:self-start shrink-0 flex-col bg-white border-r border-softBorder`}
            >
              <TenantTree
                tenants={visibleTenants}
                selectedTenantId={selectedTenantObj ? selectedTenantObj.tenantId : null}
                selectedLocationId={selectedLocationObj ? selectedLocationObj.id : null}
                loadedTenants={loadedTenants}
                loadingTenants={loadingTenants}
                showRoot={scopeRole === 'Admin'}
                showClientStatus={isAdminForLiveClient}
                canCreateLocation={(tenantId) => fallbackPermissions.canCreateLocation(tenantId)}
                onAddLocation={(tenant) => {
                  setTreeOpen(false);
                  openAddLocation(tenant.tenantId);
                }}
                onNavigate={() => setTreeOpen(false)}
              />
            </aside>
          </>
        )}
        <main className="flex-1 min-w-0" data-testid="tenant-management-pane">
          {pane}
        </main>
      </div>

      {/* New tenant (M9) — develop's Create New Tenant panel, as a modal. */}
      {fallbackPermissions.canCreateTenant && newTenantOpen && (
        <NewTenantModal
          values={newTenant}
          onChange={setNewTenant}
          onSubmit={handleCreateTenant}
          onClose={() => setNewTenantOpen(false)}
          isSubmitting={isSubmitting}
          error={error}
        />
      )}

      {/* Create Location Modal */}
      {selectedTenant && modalType === 'location' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">{editingLocationId ? 'Edit Location' : 'Add Location'}</h3>
              <button
                onClick={() => {
                  setSelectedTenant(null);
                  setEditingLocationId(null);
                  setModalType(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <form onSubmit={editingLocationId ? handleUpdateLocation : handleCreateLocation}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="location_name">
                  Location Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="location_name"
                  name="name"
                  type="text"
                  value={newLocation.name}
                  onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="Enter location name"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="location_timezone">
                  Timezone <span className="text-red-500">*</span>
                </label>
                <select
                  id="location_timezone"
                  name="timezone"
                  value={newLocation.timezone}
                  onChange={(e) => setNewLocation({ ...newLocation, timezone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  required
                >
                  <option value="">Select a timezone</option>
                  <option value="America/New_York">Eastern Time (America/New_York)</option>
                  <option value="America/Chicago">Central Time (America/Chicago)</option>
                  <option value="America/Denver">Mountain Time - DST (America/Denver)</option>
                  <option value="America/Phoenix">Mountain Time - No DST (America/Phoenix)</option>
                  <option value="America/Los_Angeles">Pacific Time (America/Los_Angeles)</option>
                  <option value="Pacific/Honolulu">Hawaii-Aleutian Time (Pacific/Honolulu)</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="location_auth_method">
                  Auth Method
                </label>
                <select
                  id="location_auth_method"
                  name="auth_method"
                  value={newLocation.auth_method}
                  onChange={(e) => setNewLocation({ ...newLocation, auth_method: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                >
                  <option value="phone_otp">Phone OTP</option>
                  <option value="pin">PIN</option>
                </select>
              </div>
              {/* feat-320: both date fields are EDIT only. LocationCreate drops date
                  fields silently, so showing them in Add Location would lose input. */}
              {editingLocationId && (
                <>
                  {/* feat-320: Recording start date. Mirrors the training go-live block
                      below (same structure and classes). Admin-only-editable; Corporate/
                      Manager see it read-only. Empty renders "Not yet recording". No
                      save-time warning (design NOTES §4): a future date is the normal case. */}
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="location_recording_start_date">
                      Recording start date
                    </label>
                    {isAdmin ? (
                      <>
                        <input
                          id="location_recording_start_date"
                          name="recording_start_date"
                          type="date"
                          value={newLocation.recording_start_date}
                          onChange={(e) => setNewLocation({ ...newLocation, recording_start_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded"
                        />
                        {newLocation.recording_start_date && (
                          <button
                            type="button"
                            onClick={() => setNewLocation({ ...newLocation, recording_start_date: '' })}
                            className="mt-1 text-xs text-blue-600 hover:underline"
                          >
                            Clear (not yet recording)
                          </button>
                        )}
                      </>
                    ) : (
                      <div
                        id="location_recording_start_date"
                        className="w-full px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-700"
                      >
                        {newLocation.recording_start_date || 'Not yet recording'}
                      </div>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {"First day this location's crew record. From this date (location time) the location is included in the daily exception and summary reports, weekly shout-outs and Live Servers."}
                    </p>
                    {isAdmin && !newLocation.recording_start_date && (
                      <p className="mt-1 text-xs text-gray-400 italic">Not yet recording</p>
                    )}
                  </div>
                  {/* feat-094: Bespoke go-live date. Optional calendar field.
                      Admin-only-editable (RBAC resolution 0002); Corporate/Manager
                      see it read-only. Empty renders "Not yet live". Validation is a
                      soft warn (handled in handleUpdateLocation), never a hard block. */}
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="location_bespoke_go_live_date">
                      Bespoke training go-live date
                    </label>
                    {isAdmin ? (
                      <>
                        <input
                          id="location_bespoke_go_live_date"
                          name="bespoke_go_live_date"
                          type="date"
                          value={newLocation.bespoke_go_live_date}
                          onChange={(e) => setNewLocation({ ...newLocation, bespoke_go_live_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded"
                        />
                        {newLocation.bespoke_go_live_date && (
                          <button
                            type="button"
                            onClick={() => setNewLocation({ ...newLocation, bespoke_go_live_date: '' })}
                            className="mt-1 text-xs text-blue-600 hover:underline"
                          >
                            Clear (mark "Not yet live")
                          </button>
                        )}
                      </>
                    ) : (
                      <div
                        id="location_bespoke_go_live_date"
                        className="w-full px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-700"
                      >
                        {newLocation.bespoke_go_live_date || 'Not yet live'}
                      </div>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {'First day of training. Training Progress counts days worked from this date, lesson drip starts on it, and the daily reports show training results from it (before it they show "Training starts <date>").'}
                    </p>
                    {isAdmin && !newLocation.bespoke_go_live_date && (
                      <p className="mt-1 text-xs text-gray-400 italic">Not yet live</p>
                    )}
                  </div>
                </>
              )}
              {/* feat-303 — drip override. EDIT only (Q3) and ADMIN only, fail-closed. */}
              {editingLocationId && isAdminForLiveClient && (() => {
                const t = tenants.find((x) => x.tenantId === selectedTenant);
                const tEnabled = !!t?.dripEnabled;
                const tRate = t?.dripLessonsPerDay ?? 1;
                const setChoice = (choice: LocationDripChoice) =>
                  setLocationDripForm((f) => ({ ...f, choice, invalid: false }));
                const errId = 'location-drip-rate-error';
                return (
                  <fieldset className="mb-4" data-testid="location-drip-fieldset">
                    {/* feat-307 (D7): DRIP_LOCATION_HELP moved behind an ⓘ beside the legend. The legend
                        stays the fieldset's first child (so it still names the radio group) and is
                        floated so the ⓘ can sit on its line without joining its accessible name. */}
                    <legend className="float-left mr-1 mb-2 text-gray-700">{DRIP_LOCATION_LEGEND}</legend>
                    <InfoPopover label={`What does ${DRIP_LOCATION_LEGEND} do here?`} testId="location-drip-help" align="left">
                      <p>{DRIP_LOCATION_HELP}</p>
                    </InfoPopover>
                    <div className="clear-both" />
                    <div className="space-y-1.5 text-sm text-gray-800">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="location_drip_mode"
                          value="inherit"
                          data-testid="location-drip-inherit"
                          checked={locationDripForm.choice === 'inherit'}
                          onChange={() => setChoice('inherit')}
                          className="h-4 w-4"
                        />
                        <span>
                          {DRIP_LOCATION_INHERIT}
                          <span className="text-gray-500">{dripLocationInheritDetail(tEnabled, tRate)}</span>
                        </span>
                      </label>
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="location_drip_mode"
                            value="on"
                            data-testid="location-drip-on"
                            checked={locationDripForm.choice === 'on'}
                            onChange={() => setChoice('on')}
                            className="h-4 w-4"
                          />
                          <span>{DRIP_LOCATION_ON}</span>
                        </label>
                        {locationDripForm.choice === 'on' && (
                          <label className="flex items-center gap-1.5 text-gray-700">
                            <span className="text-gray-400" aria-hidden="true">·</span>
                            <input
                              ref={locationDripRateRef}
                              type="number"
                              min={DRIP_RATE_MIN}
                              max={DRIP_RATE_MAX}
                              step={1}
                              inputMode="numeric"
                              data-testid="location-drip-rate"
                              aria-label={DRIP_LOCATION_RATE_ARIA}
                              aria-invalid={locationDripForm.invalid || undefined}
                              aria-describedby={locationDripForm.invalid ? errId : undefined}
                              value={locationDripForm.rate}
                              onChange={(e) => {
                                const rate = e.target.value;
                                setLocationDripForm((f) => ({ ...f, rate, invalid: false }));
                              }}
                              className={`w-14 px-2 py-0.5 border ${
                                locationDripForm.invalid ? 'border-red-500' : 'border-gray-300'
                              } rounded text-sm`}
                            />
                            {DRIP_RATE_SUFFIX}
                          </label>
                        )}
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="location_drip_mode"
                          value="off"
                          data-testid="location-drip-off"
                          checked={locationDripForm.choice === 'off'}
                          onChange={() => setChoice('off')}
                          className="h-4 w-4"
                        />
                        <span>{DRIP_LOCATION_OFF}</span>
                      </label>
                    </div>
                    {locationDripForm.invalid && (
                      <p id={errId} role="alert" data-testid="location-drip-rate-error" className="mt-1 text-xs text-red-700">
                        {DRIP_RATE_INVALID}
                      </p>
                    )}
                  </fieldset>
                );
              })()}
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex-1 ${editingLocationId ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'} text-white py-2 rounded ${
                    isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isSubmitting ? (editingLocationId ? "Saving..." : "Creating...") : (editingLocationId ? "Save Changes" : "Create Location")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedTenant(null);
                    setEditingLocationId(null);
                    setModalType(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* feat-022-consent-evidence: Withdraw Consent Modal — captures a
          mandatory reason before clearing consent + resetting onboarding. */}
      {withdrawTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Withdraw consent</h3>
              <button
                onClick={() => {
                  setWithdrawTarget(null);
                  setWithdrawReason('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Withdraw recording consent for{' '}
              <span className="font-medium">{withdrawTarget.serverName}</span>? This clears their
              consent and resets onboarding, so they will be re-presented with the consent step on
              next login. This action is recorded in the audit trail and cannot be undone.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleWithdrawConsent();
              }}
            >
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="withdraw_reason">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="withdraw_reason"
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  rows={3}
                  placeholder="Why is consent being withdrawn? (recorded in the audit trail)"
                  required
                  autoFocus
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={isSubmitting || !withdrawReason.trim()}
                  className={`flex-1 bg-red-600 text-white py-2 rounded hover:bg-red-700 ${
                    isSubmitting || !withdrawReason.trim() ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Withdrawing…' : 'Withdraw consent'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setWithdrawTarget(null);
                    setWithdrawReason('');
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Server Modal */}
      {selectedTenant && selectedLocation && modalType === 'server' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          {/* feat-308 Part B: pinned header, scrolling body, pinned footer. The
              card is a flex column capped at 90vh; only server-modal-body
              scrolls. The footer stays inside the <form> so Save (and Enter
              in a field) still submits. */}
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
            data-testid="server-modal"
          >
            <div className="flex justify-between items-center flex-shrink-0 px-6 pt-6 pb-4" data-testid="server-modal-header">
              <h3 className="text-xl font-semibold">{editingServerId ? 'Edit Server' : 'Add Server'}</h3>
              <button
                onClick={() => {
                  setSelectedTenant(null);
                  setSelectedLocation(null);
                  setEditingServerId(null);
                  setNewServer(emptyNewServer()); // feat-301 QA: closing discards the form
                  setModalType(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <form
              onSubmit={editingServerId ? handleUpdateServer : handleCreateServer}
              className="flex flex-col flex-1 min-h-0"
              data-testid="server-modal-form"
            >
              <div className="flex-1 min-h-0 overflow-y-auto px-6" data-testid="server-modal-body">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 mb-2" htmlFor="first_name">
                      First Name
                    </label>
                    <input
                      id="first_name"
                      name="first_name"
                      type="text"
                      value={newServer.first_name}
                      onChange={(e) => setNewServer(prev => ({ ...prev, first_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2" htmlFor="last_name">
                      Last Name
                    </label>
                    <input
                      id="last_name"
                      name="last_name"
                      type="text"
                      value={newServer.last_name}
                      onChange={(e) => setNewServer(prev => ({ ...prev, last_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                      placeholder="Last name"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="phone">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={newServer.phone}
                    onChange={(e) => setNewServer(prev => ({ ...prev, phone: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded ${
                      newServer.phone && !isValidPhone(newServer.phone)
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    placeholder="e.g. (555) 123-4567"
                  />
                  {newServer.phone && !isValidPhone(newServer.phone) && (
                    <p className="text-red-500 text-sm mt-1">Please enter a valid 10-digit US phone number</p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="pin">
                    PIN <span className="text-gray-400 text-sm font-normal">(4-6 digits, for PIN-auth locations)</span>
                  </label>
                  <input
                    id="pin"
                    name="pin"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{4,6}"
                    maxLength={6}
                    value={newServer.pin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setNewServer(prev => ({ ...prev, pin: val }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded font-mono"
                    placeholder="e.g. 1234"
                  />
                  {newServer.pin && (newServer.pin.length < 4 || newServer.pin.length > 6) && (
                    <p className="text-red-500 text-sm mt-1">PIN must be 4-6 digits</p>
                  )}
                </div>
                {/* feat-301-server-multiple-roles: role set + primary (design R1–R10). */}
                <ServerRolesField
                  tenantRoles={tenantRoles}
                  status={tenantRolesStatus}
                  roles={newServer.roles}
                  primary={newServer.default_role}
                  onChange={({ roles, primary }) =>
                    setNewServer(prev => ({ ...prev, roles, default_role: primary }))
                  }
                />
                {/* feat-038-login-locale (ADR 0004 Q3): per-server language preference.
                    Edit-only so it never overrides create_server's location-default seed.
                    Persists through the existing update_server PUT (preferred_locale). */}
                {editingServerId && (
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="preferred_locale">
                      Language
                    </label>
                    <select
                      id="preferred_locale"
                      name="preferred_locale"
                      value={newServer.preferred_locale}
                      onChange={(e) => setNewServer(prev => ({ ...prev, preferred_locale: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    >
                      {SUPPORTED_LANGUAGES.map((l) => (
                        <option key={l.code} value={l.code}>
                          {l.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">
                      The language this server sees in the app. Falls back to the location default, then English.
                    </p>
                  </div>
                )}
                {/* feat-155-shift-location-on-record (ADR 0001 D4): reassign the server's home
                    location. Edit-only — the create flow already seeds location from context. The
                    chosen location becomes server.location_id via the extended update_server PUT
                    (backend fail-closes 404 on a location outside the caller's tenant). */}
                {editingServerId && (
                  <div className="mb-4">
                    <label className="block text-gray-700 mb-2" htmlFor="location_id">
                      Location
                    </label>
                    <select
                      id="location_id"
                      name="location_id"
                      value={newServer.location_id}
                      onChange={(e) => setNewServer(prev => ({ ...prev, location_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                    >
                      {(tenants.find(t => t.tenantId === selectedTenant)?.locations || []).map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">
                      The server's home location. Changing it does <span className="font-medium">not</span> move
                      their past shifts — shift history is frozen per-shift. Only reassign after the location
                      migration (099_) is live in production.
                    </p>
                  </div>
                )}
                {/* feat-155-shift-location-on-record (ADR 0001 D4): multi-location flag. When on, the
                    Flutter app prompts this server to pick a location at shift start; single-location
                    servers see no picker. Edit-only. Persists via update_server PUT (is_multi_location). */}
                {editingServerId && (
                  <div className="mb-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newServer.is_multi_location}
                        onChange={(e) => setNewServer(prev => ({ ...prev, is_multi_location: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="relative flex-shrink-0 w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="text-gray-700">
                        Multi-location
                        <span className="block text-xs text-gray-400">Prompts this server to pick a location when they start a shift.</span>
                      </span>
                    </label>
                  </div>
                )}
                <div className="mb-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newServer.notifications}
                      onChange={(e) => setNewServer(prev => ({ ...prev, notifications: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="relative flex-shrink-0 w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="text-gray-700">Notifications</span>
                  </label>
                </div>
                <div className="mb-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newServer.demo}
                      onChange={(e) => setNewServer(prev => ({ ...prev, demo: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="relative flex-shrink-0 w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="text-gray-700">
                      Demo
                      <span className="block text-xs text-gray-400">Transcripts analyzed every 10s instead of 30s.</span>
                    </span>
                  </label>
                </div>
                {editingServerId && (
                  <div className="mb-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newServer.test}
                        onChange={(e) => setNewServer(prev => ({ ...prev, test: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="relative flex-shrink-0 w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="text-gray-700">
                        Test
                        <span className="block text-xs text-gray-400">For Bespoke employees — can log in to the staging environment; sorted to top of server lists, no processing changes.</span>
                      </span>
                    </label>
                  </div>
                )}
                {editingServerId && (() => {
                  const currentServer = tenants
                    .find(t => t.tenantId === selectedTenant)?.locations
                    .find(l => l.id === selectedLocation)?.servers
                    .find(s => s.id === editingServerId);
                  if (!currentServer) return null;
                  const rowBase = "flex items-center justify-between border border-gray-200 rounded px-3 py-2 mb-2 text-sm";
                  return (
                    <div className="mb-4 border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Progress</h4>
                      <div className={rowBase}>
                        <span className="text-gray-700">
                          Training:{' '}
                          <span className={currentServer.trainingComplete ? 'text-green-600 font-medium' : 'text-gray-500'}>
                            {currentServer.trainingComplete ? 'Complete' : 'Incomplete'}
                          </span>
                        </span>
                        {currentServer.trainingComplete && selectedTenant && selectedLocation && (
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => handleResetServerFlag(selectedTenant, selectedLocation, editingServerId, 'training_complete')}
                            className={`text-red-600 hover:text-red-800 text-sm px-2 py-1 border border-red-300 rounded ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            Reset
                          </button>
                        )}
                      </div>
                      <div className={rowBase}>
                        <span className="text-gray-700">
                          Onboarding:{' '}
                          <span className={currentServer.onboardingComplete ? 'text-green-600 font-medium' : 'text-gray-500'}>
                            {currentServer.onboardingComplete ? 'Complete' : 'Incomplete'}
                          </span>
                        </span>
                        {currentServer.onboardingComplete && selectedTenant && selectedLocation && (
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => handleResetServerFlag(selectedTenant, selectedLocation, editingServerId, 'onboarding_complete')}
                            className={`text-red-600 hover:text-red-800 text-sm px-2 py-1 border border-red-300 rounded ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            Reset
                          </button>
                        )}
                      </div>
                      {/* feat-022-consent-evidence: Withdraw consent is a distinct
                          control, shown only when consent is currently given (ADR
                          0001 §6/§9). It opens a modal that captures a mandatory
                          reason, then clears consent + resets onboarding. */}
                      <div className={rowBase}>
                        <span className="text-gray-700">
                          Consent:{' '}
                          <span className={currentServer.consentGiven ? 'text-green-600 font-medium' : 'text-gray-500'}>
                            {currentServer.consentGiven ? 'Given' : 'Not given'}
                          </span>
                        </span>
                        {currentServer.consentGiven && selectedTenant && selectedLocation &&
                          fallbackPermissions.canEditServer(selectedTenant, selectedLocation, editingServerId) && (
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => {
                              setWithdrawReason('');
                              setWithdrawTarget({
                                tenantId: selectedTenant,
                                locationId: selectedLocation,
                                serverId: editingServerId,
                                serverName: currentServer.name,
                              });
                            }}
                            className={`text-red-600 hover:text-red-800 text-sm px-2 py-1 border border-red-300 rounded ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            Withdraw consent
                          </button>
                        )}
                      </div>
                      {/* feat-258-tenant-management-unbound-server-lookups: wipe this server's
                          Learn-module state. Preston ruled on 2026-08-23 that this is
                          test-servers-only — the reset is destructive and not meaningfully
                          reversible, so it must not reach a real employee regardless of tenant.
                          The backend enforces it with a 403 ("This action is only available for
                          test servers").

                          ALWAYS rendered — for a non-test server it renders DISABLED rather than
                          hidden, and the grey note is the whole explanation an admin gets. The
                          gate reads `currentServer.test`, the PERSISTED record, not the unsaved
                          `newServer.test` toggle higher up this form: gating on form state would
                          let the button look enabled while the backend still 403s. Flip the
                          toggle, Save, and the button turns on.

                          Deliberately NOT gated on canEditServer — the Curriculum button below
                          is not either, and Preston ruled on 2026-08-23 to leave that out of this
                          feature. It is a recorded gap, not a dropped one. */}
                      <div className={`${rowBase} flex-wrap`}>
                        <span className="text-gray-700">Learn module</span>
                        {selectedTenant && selectedLocation && (
                          <button
                            type="button"
                            disabled={isSubmitting || !currentServer.test}
                            onClick={() => handleResetLearnProgress(selectedTenant, selectedLocation, editingServerId)}
                            className={`text-red-600 hover:text-red-800 text-sm px-2 py-1 border border-red-300 rounded ${(isSubmitting || !currentServer.test) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            Reset
                          </button>
                        )}
                        {/* security-feat-258 NIT: the note lives INSIDE the
                            selectedTenant && selectedLocation guard. Outside it, the note
                            explained a restriction on a button that was not on screen.
                            (The feat-255 Curriculum row below still has the outside-the-guard
                            shape; noted for the follow-up rather than changed here.) */}
                        {selectedTenant && selectedLocation && (
                          <span className="block w-full mt-1 text-xs text-gray-400">
                            Permanently deletes all Learn history for this server — quiz and rehearsal
                            attempts, mastery, lesson completions, Learn XP and lesson badges.
                            Restricted to test servers because it cannot be undone on a real employee.
                            Available once the Test toggle above is saved on.
                          </span>
                        )}
                      </div>
                      {/* feat-255-pass-servers-thru-content: mark all active drills in this
                          tenant complete, so a Bespoke employee testing the app reaches newly
                          authored content without grinding the curriculum. Sits directly
                          under the Learn module Reset above, which is its undo path.

                          ALWAYS rendered — for a non-test server it renders DISABLED rather
                          than hidden, and the grey note is the whole explanation an admin
                          gets. The gate reads `currentServer.test`, the PERSISTED record, not
                          the unsaved `newServer.test` toggle higher up this form: gating on
                          form state would let the button look enabled while the backend still
                          403s. Flip the toggle, Save, and the button turns on. */}
                      <div className={`${rowBase} flex-wrap`}>
                        <span className="text-gray-700">Curriculum</span>
                        {selectedTenant && selectedLocation && (
                          <button
                            type="button"
                            disabled={isSubmitting || !currentServer.test}
                            onClick={() => handleCompleteLearnProgress(selectedTenant, selectedLocation, editingServerId)}
                            className={`text-blue-600 hover:text-blue-800 text-sm px-2 py-1 border border-blue-300 rounded ${(isSubmitting || !currentServer.test) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            Mark all complete
                          </button>
                        )}
                        <span className="block w-full mt-1 text-xs text-gray-400">
                          Marks every active drill in this tenant as passed, so a tester can jump
                          straight to new content. Available once the Test toggle above is saved on.
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="flex-shrink-0 px-6 pt-2 pb-6" data-testid="server-modal-footer">
                {/* feat-155 fast-follow §2: surface the backend error detail inline (any
                    non-2xx from the create/update PUT — e.g. the 409 duplicate-PIN move).
                    fetchApi throws Error(detail), handlers setError(err.message); the global
                    banner sits behind this fixed-overlay modal, so render it here near Save. */}
                {error && (
                  <div className="mb-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                    {error}
                  </div>
                )}
                {/* feat-255-pass-servers-thru-content: success line for "Mark all
                    complete", deliberately in the same place as the error banner
                    above so both outcomes of the same click land in one spot. */}
                {learnCompleteNotice && (
                  <div
                    className="mb-4 rounded border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-700"
                    role="status"
                    data-testid="learn-complete-notice"
                  >
                    {learnCompleteNotice}
                  </div>
                )}
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`flex-1 ${editingServerId ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-500 hover:bg-green-600'} text-white py-2 rounded ${
                      isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {isSubmitting
                      ? (editingServerId ? "Saving..." : "Creating...")
                      : (editingServerId ? "Save Changes" : "Create Server")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedTenant(null);
                      setSelectedLocation(null);
                      setEditingServerId(null);
                      setNewServer(emptyNewServer()); // feat-301 QA: closing discards the form
                      setModalType(null);
                      setManagerError(null);
                    }}
                    className="flex-1 bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* XP Audit Modal */}
      {modalType === 'server-audit' && auditingServerId && (() => {
        const sourceLabel = (s: string) => {
          switch (s) {
            case 'shift_interaction': return 'Shift interaction';
            case 'quiz': return 'Quiz';
            case 'gift_received': return 'Gift received';
            case 'gift_sent': return 'Gift sent';
            case 'reward_redeemed': return 'Reward redeemed';
            case 'learn_reset': return 'Learn reset';
            default: return s;
          }
        };
        const sourceEmoji = (s: string) => {
          switch (s) {
            case 'shift_interaction': return '🎙️';
            case 'quiz': return '📚';
            case 'gift_received':
            case 'gift_sent': return '🎁';
            case 'reward_redeemed': return '💎';
            case 'learn_reset': return '♻️';
            default: return '•';
          }
        };
        const formatDescription = (e: NonNullable<typeof auditData>["entries"][number]) => {
          // feat-198 (ADR 0001 §D-K): `source === 'quiz'` STAYS. It is a
          // `gem_transaction.source` enum token, not a table name — no table is
          // called `quiz` — so its meaning does not change and rewriting it
          // would be an XP-ledger data migration for a cosmetic gain.
          if (e.source === 'quiz' && e.drill_title) {
            const score = e.quiz_score != null && e.quiz_total != null ? ` (${e.quiz_score}/${e.quiz_total})` : '';
            return `${e.drill_emoji ?? ''} ${e.drill_title}${score}`.trim();
          }
          if (e.source === 'shift_interaction' && e.interaction_type) {
            return e.interaction_type.replace(/_/g, ' ');
          }
          if (e.source === 'gift_received' && e.gift_from_name) {
            return `${e.gift_label || 'Gift'} — from ${e.gift_from_name}`;
          }
          if (e.source === 'gift_sent' && e.gift_to_name) {
            return `${e.gift_label || 'Gift'} — to ${e.gift_to_name}`;
          }
          if (e.source === 'reward_redeemed' && e.reward_name) {
            return `${e.reward_emoji ?? ''} ${e.reward_name}`.trim();
          }
          return e.description || '—';
        };
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xl font-semibold">XP History — {auditingServerName}</h3>
                <button
                  onClick={() => {
                    setModalType(null);
                    setAuditingServerId(null);
                    setAuditData(null);
                    setAuditError(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Running total reflects the gem ledger; it may differ slightly from the profile XP if recent shift interactions haven&rsquo;t been processed yet.
              </p>

              {auditLoading && (
                <div className="text-center py-8 text-gray-500">Loading audit…</div>
              )}
              {auditError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded mb-3">
                  {auditError}
                </div>
              )}
              {auditData && !auditLoading && (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
                    <div className="bg-green-50 border border-green-200 rounded p-2">
                      <div className="text-gray-500">Total earned</div>
                      <div className="font-semibold text-green-700">+{auditData.total_earned}</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded p-2">
                      <div className="text-gray-500">Total spent</div>
                      <div className="font-semibold text-red-700">-{auditData.total_spent}</div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded p-2">
                      <div className="text-gray-500">Net (ledger)</div>
                      <div className="font-semibold">{auditData.net_balance}</div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-auto border border-gray-200 rounded">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="py-2 px-3 text-left">When</th>
                          <th className="py-2 px-3 text-left">Source</th>
                          <th className="py-2 px-3 text-left">Description</th>
                          <th className="py-2 px-3 text-right">Amount</th>
                          <th className="py-2 px-3 text-right">Running total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditData.entries.length === 0 && (
                          <tr>
                            <td colSpan={5} className="py-6 text-center text-gray-500">
                              No XP transactions yet.
                            </td>
                          </tr>
                        )}
                        {auditData.entries.map((e) => (
                          <tr key={e.id} className="border-t border-gray-200">
                            <td className="py-2 px-3 whitespace-nowrap">
                              {e.created ? new Date(e.created).toLocaleString() : '—'}
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                              <span className="mr-1">{sourceEmoji(e.source)}</span>
                              {sourceLabel(e.source)}
                            </td>
                            <td className="py-2 px-3">{formatDescription(e)}</td>
                            <td className={`py-2 px-3 text-right font-mono ${e.amount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                              {e.amount >= 0 ? `+${e.amount}` : e.amount}
                            </td>
                            <td className="py-2 px-3 text-right font-mono">
                              {e.balance_after ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Create Manager Modal */}
      {selectedTenant && modalType === 'manager' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                Add {selectedLocation ? 'Store Manager' : 'Corporate Manager'}
              </h3>
              <button
                onClick={() => {
                  setSelectedTenant(null);
                  setSelectedLocation(null);
                  setModalType(null);
                  setManagerError(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            {managerError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p>{managerError}</p>
              </div>
            )}
            <form onSubmit={handleCreateManager}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="manager_email">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="manager_email"
                  name="email"
                  type="email"
                  value={newManager.email}
                  onChange={(e) => setNewManager(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded ${
                    newManager.email && !isValidEmail(newManager.email) 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-300'
                  }`}
                  placeholder="manager@example.com"
                  required
                />
                {newManager.email && !isValidEmail(newManager.email) && (
                  <p className="text-red-500 text-sm mt-1">Please enter a valid email address</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="manager_first_name">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="manager_first_name"
                    name="first_name"
                    type="text"
                    value={newManager.first_name}
                    onChange={(e) => setNewManager(prev => ({ ...prev, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="First name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="manager_last_name">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="manager_last_name"
                    name="last_name"
                    type="text"
                    value={newManager.last_name}
                    onChange={(e) => setNewManager(prev => ({ ...prev, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="manager_phone">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  id="manager_phone"
                  name="phone"
                  type="tel"
                  value={newManager.phone}
                  onChange={(e) => setNewManager(prev => ({ ...prev, phone: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded ${
                    newManager.phone && !isValidPhone(newManager.phone) 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-300'
                  }`}
                  placeholder="e.g. (555) 123-4567"
                  required
                />
                {newManager.phone && !isValidPhone(newManager.phone) && (
                  <p className="text-red-500 text-sm mt-1">Please enter a valid 10-digit US phone number</p>
                )}
              </div>
              {/* Role selector removed; role is driven by context */}
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex-1 bg-purple-500 text-white py-2 rounded hover:bg-purple-600 ${
                    isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isSubmitting ? "Creating..." : "Create Manager"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedTenant(null);
                    setSelectedLocation(null);
                    setModalType(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Manager Shift Modal */}
      {selectedTenant && selectedLocation && modalType === 'manager-shift' && (() => {
        const tenant = tenants.find(t => t.tenantId === selectedTenant);
        const location = tenant?.locations.find(l => l.id === selectedLocation);
        const allSelected = location ? newManagerShift.server_ids.length === location.servers.length && location.servers.length > 0 : false;
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">{editingManagerShiftId ? 'Edit Manager Shift' : 'Add Manager Shift'}</h3>
                <button
                  onClick={() => { setSelectedTenant(null); setSelectedLocation(null); setModalType(null); setEditingManagerShiftId(null); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <form onSubmit={handleSaveManagerShift}>
                {/* Manager dropdown */}
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Manager <span className="text-red-500">*</span></label>
                  <select
                    value={newManagerShift.manager_id}
                    onChange={(e) => setNewManagerShift(prev => ({ ...prev, manager_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  >
                    <option value="">Select a manager</option>
                    {location?.managers.map(m => (
                      <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
                    ))}
                  </select>
                </div>

                {/* Server multi-select */}
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Servers <span className="text-red-500">*</span></label>
                  <div className="border border-gray-300 rounded p-3 max-h-48 overflow-y-auto">
                    <label className="flex items-center mb-2 pb-2 border-b border-gray-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => {
                          if (allSelected) {
                            setNewManagerShift(prev => ({ ...prev, server_ids: [] }));
                          } else {
                            setNewManagerShift(prev => ({ ...prev, server_ids: location?.servers.map(s => s.id) || [] }));
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium">Select All</span>
                    </label>
                    {location?.servers.map(s => (
                      <label key={s.id} className="flex items-center mb-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newManagerShift.server_ids.includes(s.id)}
                          onChange={() => {
                            setNewManagerShift(prev => ({
                              ...prev,
                              server_ids: prev.server_ids.includes(s.id)
                                ? prev.server_ids.filter(id => id !== s.id)
                                : [...prev.server_ids, s.id]
                            }));
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">{s.name}</span>
                      </label>
                    ))}
                    {(!location?.servers || location.servers.length === 0) && (
                      <p className="text-gray-400 text-sm">No servers in this location</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{newManagerShift.server_ids.length} selected</p>
                </div>

                {/* Type */}
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Type <span className="text-red-500">*</span></label>
                  <select
                    value={newManagerShift.shift_type}
                    onChange={(e) => setNewManagerShift(prev => ({ ...prev, shift_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  >
                    <option value="Lunch">Lunch</option>
                    <option value="Dinner">Dinner</option>
                  </select>
                </div>

                {/* Start date/time */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 mb-2">Start <span className="text-red-500">*</span></label>
                    <input
                      type="datetime-local"
                      value={newManagerShift.start_time}
                      onChange={(e) => setNewManagerShift(prev => ({ ...prev, start_time: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2">End <span className="text-red-500">*</span></label>
                    <input
                      type="datetime-local"
                      value={newManagerShift.end_time}
                      onChange={(e) => setNewManagerShift(prev => ({ ...prev, end_time: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded"
                      required
                    />
                  </div>
                </div>

                {/* Notes */}
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Notes</label>
                  <input
                    type="text"
                    value={newManagerShift.notes}
                    onChange={(e) => setNewManagerShift(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Optional notes"
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`flex-1 bg-indigo-500 text-white py-2 rounded hover:bg-indigo-600 ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isSubmitting ? "Saving..." : editingManagerShiftId ? "Update Manager Shift" : "Create Manager Shift"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSelectedTenant(null); setSelectedLocation(null); setModalType(null); setEditingManagerShiftId(null); }}
                    className="flex-1 bg-gray-200 text-gray-800 py-2 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Password Popup Modal */}
      {passwordPopup?.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-green-600">
                Manager Created Successfully
              </h3>
            </div>
            
            <div className="mb-6">
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Manager Information:</h4>
                <p className="text-sm text-gray-700">
                  <strong>Name:</strong> {passwordPopup.firstName} {passwordPopup.lastName}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Email:</strong> {passwordPopup.email}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Role:</strong> {passwordPopup.role}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Phone:</strong> {passwordPopup.phone}
                </p>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-yellow-800 mb-2">Temporary Password:</h4>
                <div className="bg-white border border-yellow-300 p-3 rounded font-mono text-lg font-bold text-center text-yellow-900">
                  {passwordPopup.password}
                </div>
                <p className="text-sm text-yellow-700 mt-2 text-center">
                  <strong>Save this password now. This is the only time it will be displayed.</strong>
                </p>
              </div>
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setPasswordPopup(null);
                }}
                className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600 font-medium"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Demo Setup Job Viewer Modal */}
      {viewingDemoJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Demo Setup Job — {viewingDemoJob.tenantName}
              </h2>
              <button
                onClick={() => setViewingDemoJob(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="max-h-[80vh] overflow-y-auto">
              {viewingDemoJob.loading && (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading demo setup job...</p>
                </div>
              )}
              {viewingDemoJob.error && (
                <div className="p-6">
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    <p className="font-medium">Failed to load</p>
                    <p className="text-sm">{viewingDemoJob.error}</p>
                  </div>
                </div>
              )}
              {viewingDemoJob.data && <DemoSetupJobViewer job={viewingDemoJob.data} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}